import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDndMonitor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createPortal } from 'react-dom';
import {
  isActionItemDragId,
  parseActionItemDragId,
} from '../utils/todoDragIds';
import {
  Archive,
  ArrowLeft,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Columns3,
  Eye,
  EyeOff,
  FolderPlus,
  FolderTree,
  GripVertical,
  Inbox,
  List,
  ListTree,
  Loader2,
  Rows3,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { TaskViewProvider, useTaskView } from '../context/TaskViewContext';
import { TaskCard } from '../components/tasks/TaskCard';
import { useTasks } from '../hooks/useTasks';
import { useDayFocus } from '../hooks/useDayFocus';
import { useTodoProjects } from '../hooks/useTodoProjects';
import { getTodayDateKey, updateDayFocusStatus } from '../services/dayFocus';
import { updateTaskStatus } from '../services/tasks';
import {
  cascadeProjectSwimLane,
  restoreCardSwimLanes,
  type CascadeAffectedCard,
} from '../services/todoProjects';
import { brandColors } from '@/design';
import type {
  ActionItemStatus,
  DayFocus,
  TaskPlan,
  TodoProject,
  TodoProjectColorKey,
  TodoSwimLane,
} from '../types';
import {
  TODO_SWIM_LANE_META,
  TODO_LANE_LABELS,
  TODO_PROJECT_COLOR_KEYS,
  TODO_PROJECT_COLOR_TOKENS,
  effectiveSwimLaneDayFocus,
  effectiveSwimLaneTaskPlan,
  isFocusFullyCompleted,
  isTaskListFullyCompleted as isTaskListFullyCompletedShared,
  sortProjectsForPivot,
  taskHasMixedOpenStatuses,
  workspaceKanbanColumnForTask,
} from '../utils/todoSwimLanes';
import { ProjectAssignButton } from '../components/todos/ProjectAssignButton';
import {
  ProjectsPivot,
  ProjectHeaderMenu,
  PROJECT_COLUMN_DROP_PREFIX,
  PROJECT_SWITCHER_INBOX_KEY,
  type ProjectsPivotRow,
} from '../components/todos/ProjectsPivot';
import { CascadeConfirmSheet } from '../components/todos/CascadeConfirmSheet';
import {
  CascadeUndoToast,
  type CascadeToastState,
} from '../components/todos/CascadeUndoToast';
import {
  BulletsCanvas,
  type BulletsRow,
  parseBulletDragId,
  parseBulletIndentDrop,
  parseBulletLaneDrop,
} from '../components/todos/BulletsCanvas';
import { PastDaySection } from '../components/todos/PastDaySection';
import { ScheduleDateStrip } from '../components/todos/ScheduleDateStrip';
import { ScheduleDropZones } from '../components/todos/ScheduleDropZones';
import { TopBarMenu, type TopBarMenuItem } from '../components/todos/TopBarMenu';
import type { TaskCardViewMode } from '../components/tasks/TaskCard';

const WS_COLUMNS: Array<{ id: ActionItemStatus; label: string }> = [
  { id: 'todo', label: 'To do' },
  { id: 'in_progress', label: 'Doing' },
  { id: 'done', label: 'Done' },
];

const LANES: TodoSwimLane[] = ['today', 'this_week', 'later'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Items in **Later** with created date older than this many calendar days slide into archive (still in Later, hidden until expanded). */
const LATER_ARCHIVE_AFTER_DAYS = 7;

const isTaskListFullyCompleted = isTaskListFullyCompletedShared;

// ─── Grouping + lane-collapse persistence ───────────────────────────────────

type TodosGrouping = 'schedule' | 'project';
const GROUPING_STORAGE_KEY = 'flank-todos-grouping-v1';
const LANE_COLLAPSED_STORAGE_KEY = 'flank-todos-lane-collapsed-v1';
const SELECTED_PROJECT_STORAGE_KEY = 'flank-todos-selected-project-v1';

/** Read the persisted project switcher selection.
 *  - `null` = "All projects" (legacy stacked view)
 *  - `PROJECT_SWITCHER_INBOX_KEY` = "Inbox" band only
 *  - any other string = a project id (validated against current projects elsewhere)
 */
function readSelectedProjectKey(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SELECTED_PROJECT_STORAGE_KEY);
    if (!raw) return null;
    return raw;
  } catch {
    return null;
  }
}

function writeSelectedProjectKey(value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(SELECTED_PROJECT_STORAGE_KEY);
    else window.localStorage.setItem(SELECTED_PROJECT_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

function readGrouping(): TodosGrouping {
  if (typeof window === 'undefined') return 'schedule';
  try {
    const raw = window.localStorage.getItem(GROUPING_STORAGE_KEY);
    if (raw === 'project' || raw === 'schedule') return raw;
  } catch {
    /* ignore quota / privacy mode */
  }
  return 'schedule';
}

function writeGrouping(value: TodosGrouping): void {
  try {
    window.localStorage.setItem(GROUPING_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

/** Default: only Today is open; This week + Later are collapsed (PRD §6.2). */
function defaultLaneCollapsed(): Record<TodoSwimLane, boolean> {
  return { today: false, this_week: true, later: true };
}

function readLaneCollapsed(): Record<TodoSwimLane, boolean> {
  const fallback = defaultLaneCollapsed();
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(LANE_COLLAPSED_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Record<TodoSwimLane, unknown>>;
    return {
      today: parsed.today === true,
      this_week: parsed.this_week !== false, // default collapsed
      later: parsed.later !== false,         // default collapsed
    };
  } catch {
    return fallback;
  }
}

function writeLaneCollapsed(state: Record<TodoSwimLane, boolean>): void {
  try {
    window.localStorage.setItem(LANE_COLLAPSED_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function dayFocusToTaskPlan(focus: DayFocus): TaskPlan {
  return {
    id: focus.id,
    userId: focus.userId,
    sessionId: focus.sessionId,
    title: focus.title,
    summary: focus.summary ?? '',
    status: focus.status,
    actionItems: focus.actionItems,
    swimLane: focus.swimLane,
    projectId: focus.projectId,
    dueDate: focus.dueDate,
    createdAt: focus.createdAt,
    updatedAt: focus.updatedAt,
    completedAt: focus.completedAt,
  };
}

function updatedAtMs(plan: TaskPlan): number {
  const ts = plan.updatedAt as { toDate?: () => Date; seconds?: number } | undefined;
  if (ts?.toDate) return ts.toDate().getTime();
  if (typeof ts?.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function createdAtToDate(plan: TaskPlan): Date | null {
  const ts = plan.createdAt as { toDate?: () => Date; seconds?: number } | undefined;
  if (ts?.toDate) {
    const d = ts.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof ts?.seconds === 'number') return new Date(ts.seconds * 1000);
  return null;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Whole calendar days from `older` → `newer` (e.g. age of a list = diff(today, createdAt)). */
function differenceInCalendarDays(newer: Date, older: Date): number {
  const a = startOfLocalDay(newer).getTime();
  const b = startOfLocalDay(older).getTime();
  return Math.round((a - b) / 86400000);
}

function dateBadgeFromCreated(task: TaskPlan): { day: number; month: string } | undefined {
  const d = createdAtToDate(task);
  if (!d) return undefined;
  return { day: d.getDate(), month: MONTHS[d.getMonth()] };
}

function isLaterLaneArchivedByAge(row: BoardRow, lane: TodoSwimLane, now: Date): boolean {
  if (lane !== 'later') return false;
  const created = createdAtToDate(row.task);
  if (!created) return false;
  return differenceInCalendarDays(now, created) > LATER_ARCHIVE_AFTER_DAYS;
}

/** Use pre-completion `updatedAt` while a card holds/exits so the lane order doesn't flicker before vanish. */
function boardRowSortActivityMs(row: BoardRow, anchors: Record<string, number>): number {
  const k = workspaceCardKey(row);
  const pinned = anchors[k];
  return typeof pinned === 'number' ? pinned : updatedAtMs(row.task);
}

type BoardRow =
  | { kind: 'task'; id: string; task: TaskPlan }
  | { kind: 'day_focus'; id: string; focus: DayFocus; task: TaskPlan };

function parseWorkspaceDragId(raw: string): { kind: 'task' | 'day_focus'; id: string } | null {
  if (raw.startsWith('ws-drag:task:')) {
    return { kind: 'task', id: raw.slice('ws-drag:task:'.length) };
  }
  if (raw.startsWith('ws-drag:focus:')) {
    return { kind: 'day_focus', id: raw.slice('ws-drag:focus:'.length) };
  }
  // Mini-chips inside ScheduleDropZones use a distinct prefix so their
  // dnd-kit registrations don't collide with the same card's full draggable
  // shell elsewhere on the page.
  if (raw.startsWith('ws-drag-zone:task:')) {
    return { kind: 'task', id: raw.slice('ws-drag-zone:task:'.length) };
  }
  if (raw.startsWith('ws-drag-zone:focus:')) {
    return { kind: 'day_focus', id: raw.slice('ws-drag-zone:focus:'.length) };
  }
  return null;
}

function parseListLaneDrop(overId: string): TodoSwimLane | null {
  for (const lane of LANES) {
    if (overId === `ws-list-lane:${lane}`) return lane;
  }
  return null;
}

/**
 * Parse a "drop on a project switcher pill" target. Returns:
 *  - `null` for an Inbox drop (caller should call `setProject(card, null)`)
 *  - `string` projectId for a project pill drop
 *  - `undefined` if `overId` doesn't match a pill drop target at all
 */
function parseProjectPillDrop(overId: string): string | null | undefined {
  const prefix = 'ws-project-pill:';
  if (!overId.startsWith(prefix)) return undefined;
  const rest = overId.slice(prefix.length);
  if (rest === '__inbox__') return null;
  return rest;
}

/**
 * Parse a Projects-pivot Kanban column drop:
 * `ws-proj-col:${projectKey | '__inbox__'}:${status}`. `projectId` is `null`
 * when the target is the Inbox band.
 */
function parseProjectColumnDrop(
  overId: string,
): { projectId: string | null; status: ActionItemStatus } | undefined {
  if (!overId.startsWith(PROJECT_COLUMN_DROP_PREFIX)) return undefined;
  const rest = overId.slice(PROJECT_COLUMN_DROP_PREFIX.length);
  // Split into [projectKey, status]; projectKey is everything before the
  // last colon to guard against any future ids containing colons.
  const lastColon = rest.lastIndexOf(':');
  if (lastColon === -1) return undefined;
  const projectKey = rest.slice(0, lastColon);
  const status = rest.slice(lastColon + 1) as ActionItemStatus;
  if (status !== 'todo' && status !== 'in_progress' && status !== 'done') return undefined;
  return {
    projectId: projectKey === '__inbox__' ? null : projectKey,
    status,
  };
}

/** When "Hide completed lists" is on, hold the fully-done card visible before the genie exit. */
const TODO_COMPLETED_HOLD_MS = 2000;
const TODO_EXIT_ANIM_MS = 820;

/** Soft positive chime when a list finishes exiting (runs once per completion). */
function playTodoListCompleteDing() {
  try {
    if (typeof window === 'undefined') return;

    type WinAudio = Window &
      typeof globalThis & { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const w = window as WinAudio;
    const AudioCtor = w.AudioContext ?? w.webkitAudioContext;
    if (!AudioCtor) return;

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

    const ctx = new AudioCtor();
    void ctx.resume?.().catch(() => {});

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.type = 'sine';
    if (reduce) {
      oscillator.frequency.setValueAtTime(659.26, ctx.currentTime);
      gainNode.gain.setValueAtTime(0.0001, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.05, ctx.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
    } else {
      oscillator.frequency.setValueAtTime(523.25, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(783.99, ctx.currentTime + 0.09);

      gainNode.gain.setValueAtTime(0.0001, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.11, ctx.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.38);
    }

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + (reduce ? 0.15 : 0.42));

    oscillator.onended = () => ctx.close().catch(() => {});
  } catch {
    /* ignore missing audio hardware / autoplay quirks */
  }
}

function workspaceCardKey(row: BoardRow): string {
  return row.kind === 'task' ? `task:${row.task.id}` : `focus:${row.focus.id}`;
}

function workspaceRowExitShellClass(phase: 'holding' | 'exiting' | undefined): string {
  if (phase === 'exiting') return 'min-w-0 flank-todo-card-exit-outer overflow-hidden';
  if (phase === 'holding') return 'min-w-0 flank-todo-card-hold-completed';
  return 'min-w-0';
}

function parseKanbanCellDrop(overId: string): { lane: TodoSwimLane; status: ActionItemStatus } | null {
  for (const lane of LANES) {
    const prefix = `ws-kan:${lane}:`;
    if (!overId.startsWith(prefix)) continue;
    const status = overId.slice(prefix.length) as ActionItemStatus;
    if (status === 'todo' || status === 'in_progress' || status === 'done') {
      return { lane, status };
    }
  }
  return null;
}

function actionItemStatus(item: TaskPlan['actionItems'][number]): ActionItemStatus {
  if (item.status) return item.status;
  return item.completed ? 'done' : 'todo';
}

function statusesForWorkspaceColumn(
  task: TaskPlan,
  target: ActionItemStatus
): Partial<Record<string, ActionItemStatus>> {
  const map: Partial<Record<string, ActionItemStatus>> = {};
  for (const item of task.actionItems) {
    if (actionItemStatus(item) === 'done') continue;
    map[item.id] = target;
  }
  return map;
}

function WorkspaceDraggableShell({
  dragId,
  children,
}: {
  dragId: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: dragId });
  const style = transform
    ? { transform: CSS.Transform.toString(transform), zIndex: isDragging ? 20 : undefined }
    : undefined;

  // Spread the drag listeners on the entire card shell, not just the handle:
  // PointerSensor's `distance: 12` activation threshold means clicks on
  // textareas / toggles inside the card still work normally (no pointer
  // movement = no drag), but the card can be grabbed from anywhere on its
  // surface. This matches Linear / Trello / GitHub Projects behaviour and
  // makes the affordance discoverable; the GripVertical icon stays as a
  // visual cue + keyboard handle.
  //
  // KEYBOARD GUARD: dnd-kit's `KeyboardSensor` activates a drag on Space
  // and drops it on Enter. With listeners spread over the whole card,
  // typing in any textarea / input inside the card would bubble Space
  // up here and start dragging the card — which then "drops" on Enter
  // wherever keyboard navigation has landed (often the Done column,
  // hence the "everything got marked complete" bug report). We therefore
  // filter `onKeyDown` so it only reaches dnd-kit when the event target
  // is *not* an editable element.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
    }
    listeners?.onKeyDown?.(event);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      // touch-none lets dnd-kit cancel native touch scroll while dragging.
      className={`flex gap-1 min-w-0 touch-none cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-60' : ''
      }`}
      {...listeners}
      {...attributes}
      onKeyDown={handleKeyDown}
    >
      <span
        // Visual handle only; its click semantics piggy-back on the parent's
        // listeners so we don't double-register pointerdown.
        aria-hidden
        className="shrink-0 self-start mt-3 p-1 rounded-lg text-[var(--color-text-muted)] group-hover:text-[var(--color-text-secondary)]"
        title="Drag to change lane, column, or project"
      >
        <GripVertical size={18} />
      </span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function LaneDropSurface({
  dropId,
  children,
  className,
}: {
  dropId: string;
  children: ReactNode;
  className?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId });
  return (
    <div
      ref={setNodeRef}
      className={className}
      style={
        isOver
          ? {
              outline: `2px dashed ${brandColors.teal}`,
              outlineOffset: 4,
              borderRadius: 12,
            }
          : undefined
      }
    >
      {children}
    </div>
  );
}

function TodosPageInner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const todayKey = useMemo(() => getTodayDateKey(), []);
  const { taskViewMode, setTaskViewMode, kanbanOrientation, setKanbanOrientation } = useTaskView();

  const {
    allTasks,
    loading: tasksLoading,
    toggle,
    addItem,
    deleteItem,
    renameItem: renameTaskItem,
    reorder,
    setItemParent: setTaskItemParent,
    renameTask,
    createBullet: createBulletTask,
    deleteTask,
    setSwimLane: setTaskSwimLane,
    setProject: setTaskProject,
  } = useTasks(user?.uid ?? null);

  const {
    allFocuses,
    loading: focusLoading,
    toggle: toggleFocusItem,
    addItem: addFocusItem,
    deleteItem: deleteFocusItem,
    renameItem: renameFocusItem,
    reorder: reorderFocusItems,
    setItemParent: setFocusItemParent,
    renameFocus,
    deleteFocus,
    setSwimLane: setFocusSwimLane,
    setProject: setFocusProject,
  } = useDayFocus(user?.uid ?? null);

  const {
    projects,
    loading: projectsLoading,
    byId: projectsById,
    createProject,
    renameProject,
    setArchived: setProjectArchived,
    setPinned: setProjectPinned,
    countReferencingCards,
    moveAllToInbox,
    deleteProject,
  } = useTodoProjects(user?.uid ?? null);

  const [grouping, setGroupingState] = useState<TodosGrouping>(() => readGrouping());
  const setGrouping = useCallback((next: TodosGrouping) => {
    setGroupingState(next);
    writeGrouping(next);
  }, []);

  const [laneCollapsed, setLaneCollapsedState] = useState<Record<TodoSwimLane, boolean>>(
    () => readLaneCollapsed()
  );
  const toggleLaneCollapsed = useCallback((lane: TodoSwimLane) => {
    setLaneCollapsedState((prev) => {
      const next = { ...prev, [lane]: !prev[lane] };
      writeLaneCollapsed(next);
      return next;
    });
  }, []);

  /**
   * Used by the date strip — guarantees the chosen lane is expanded then
   * smooth-scrolls to its `id="ws-lane-${lane}"` anchor. The anchor is added
   * to all three Schedule renderers (List / Kanban / Bullets) so the strip
   * works regardless of the active view mode.
   */
  const jumpToLane = useCallback(
    (lane: TodoSwimLane) => {
      setLaneCollapsedState((prev) => {
        if (!prev[lane]) return prev;
        const next = { ...prev, [lane]: false };
        writeLaneCollapsed(next);
        return next;
      });
      window.requestAnimationFrame(() => {
        const el = document.getElementById(`ws-lane-${lane}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    [],
  );

  /**
   * When the user picks a past date on the Schedule date strip we swap the
   * Today / This week / Later lanes for a retrospective `PastDaySection`
   * (read-only). `selectedPastDate === null` means the strip is on today
   * (or the future) and the lanes render as normal.
   */
  const [selectedPastDate, setSelectedPastDate] = useState<Date | null>(null);
  const handleSelectScheduleDate = useCallback(
    (date: Date, info: { isPast: boolean; isToday: boolean; isFuture: boolean }) => {
      if (info.isPast) {
        setSelectedPastDate(date);
      } else {
        setSelectedPastDate(null);
        // Selecting today / a future date keeps the existing
        // jump-to-lane behaviour (handled by the strip's own callback).
      }
    },
    [],
  );

  const [hideCompletedLists, setHideCompletedLists] = useState(true);
  const [showLaterArchive, setShowLaterArchive] = useState(false);

  // Cascade dialog + toast state.
  const [cascadeProjectId, setCascadeProjectId] = useState<string | null>(null);
  const [cascadeTargetLane, setCascadeTargetLane] = useState<TodoSwimLane>('today');
  const [cascadeBusy, setCascadeBusy] = useState(false);
  const [toast, setToast] = useState<CascadeToastState | null>(null);

  // Project deletion (guarded) state.
  const [pendingDeleteProjectId, setPendingDeleteProjectId] = useState<string | null>(null);
  const [pendingDeleteCounts, setPendingDeleteCounts] = useState<{ tasks: number; focuses: number } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Project switcher (one project / Inbox at a time inside the Projects pivot).
  const [selectedProjectKey, setSelectedProjectKeyState] = useState<string | null>(
    () => readSelectedProjectKey()
  );
  const setSelectedProjectKey = useCallback((next: string | null) => {
    setSelectedProjectKeyState(next);
    writeSelectedProjectKey(next);
  }, []);

  // Bulk archive (set status='abandoned' on every active TaskPlan + DayFocus across all lanes).
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkArchiveBusy, setBulkArchiveBusy] = useState(false);

  // Create-project sheet (Flank-styled name + color picker; replaces silent default-name create).
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [createSheetBusy, setCreateSheetBusy] = useState(false);

  const [exitingWorkspaceCards, setExitingWorkspaceCards] = useState<
    Record<string, 'holding' | 'exiting'>
  >({});
  const workspaceExitTimersRef = useRef<
    Record<string, { hold?: ReturnType<typeof setTimeout>; exit?: ReturnType<typeof setTimeout> }>
  >({});
  const prevWorkspaceFullyCompleteRef = useRef<Map<string, boolean>>(new Map());
  /** Firestore bumps `updatedAt` when the user completes items — pinning this keeps the row from jumping to lane top mid-animation. */
  const frozenSortAnchorsRef = useRef<Record<string, number>>({});
  const prevUpdatedAtMsRef = useRef<Map<string, number>>(new Map());

  const prevHideCompletedListsRef = useRef(hideCompletedLists);

  const cancelWorkspaceListExit = useCallback((key: string) => {
    const timers = workspaceExitTimersRef.current[key];
    if (timers?.hold) window.clearTimeout(timers.hold);
    if (timers?.exit) window.clearTimeout(timers.exit);
    delete workspaceExitTimersRef.current[key];
    delete frozenSortAnchorsRef.current[key];
    setExitingWorkspaceCards((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const scheduleWorkspaceListExit = useCallback(
    (key: string, options?: { suppressDing?: boolean }) => {
      let didStart = false;
      setExitingWorkspaceCards((prev) => {
        if (key in prev) return prev;
        didStart = true;
        return { ...prev, [key]: 'holding' };
      });
      if (!didStart) return;

      const existing = workspaceExitTimersRef.current[key];
      if (existing?.hold) window.clearTimeout(existing.hold);
      if (existing?.exit) window.clearTimeout(existing.exit);

      const holdTimer = window.setTimeout(() => {
        if (!options?.suppressDing) playTodoListCompleteDing();
        setExitingWorkspaceCards((prev) => {
          if (!(key in prev)) return prev;
          return { ...prev, [key]: 'exiting' };
        });
        const exitTimer = window.setTimeout(() => {
          cancelWorkspaceListExit(key);
        }, TODO_EXIT_ANIM_MS);
        workspaceExitTimersRef.current[key] = { exit: exitTimer };
      }, TODO_COMPLETED_HOLD_MS);

      workspaceExitTimersRef.current[key] = { hold: holdTimer };
    },
    [cancelWorkspaceListExit]
  );

  useEffect(() => {
    const prev = prevHideCompletedListsRef.current;
    prevHideCompletedListsRef.current = hideCompletedLists;

    if (!hideCompletedLists || prev !== false) return;

    for (const t of allTasks) {
      if (t.status === 'abandoned') continue;
      if (!isTaskListFullyCompleted(t)) continue;
      scheduleWorkspaceListExit(`task:${t.id}`, { suppressDing: true });
    }
    for (const f of allFocuses) {
      if (f.status === 'abandoned') continue;
      if (!isTaskListFullyCompleted(dayFocusToTaskPlan(f))) continue;
      scheduleWorkspaceListExit(`focus:${f.id}`, { suppressDing: true });
    }
  }, [hideCompletedLists, allTasks, allFocuses, scheduleWorkspaceListExit]);

  useLayoutEffect(() => {
    if (!hideCompletedLists) {
      for (const timers of Object.values(workspaceExitTimersRef.current)) {
        if (timers.hold) window.clearTimeout(timers.hold);
        if (timers.exit) window.clearTimeout(timers.exit);
      }
      workspaceExitTimersRef.current = {};
      prevWorkspaceFullyCompleteRef.current.clear();
      prevUpdatedAtMsRef.current.clear();
      frozenSortAnchorsRef.current = {};
      setExitingWorkspaceCards({});
      return;
    }

    const prev = prevWorkspaceFullyCompleteRef.current;
    const next = new Map<string, boolean>();
    const prevTs = prevUpdatedAtMsRef.current;

    const updatedAtSnap = new Map<string, number>();

    for (const t of allTasks) {
      const key = `task:${t.id}`;
      if (t.status === 'abandoned') {
        cancelWorkspaceListExit(key);
        continue;
      }
      const currentMs = updatedAtMs(t);
      updatedAtSnap.set(key, currentMs);

      const full = isTaskListFullyCompleted(t);
      next.set(key, full);
      const prevVal = prev.get(key);
      const priorMs = prevTs.get(key) ?? currentMs;

      if (!full) {
        cancelWorkspaceListExit(key);
      } else if (prevVal === false) {
        frozenSortAnchorsRef.current[key] = priorMs;
        scheduleWorkspaceListExit(key);
      }
    }

    for (const f of allFocuses) {
      const key = `focus:${f.id}`;
      if (f.status === 'abandoned') {
        cancelWorkspaceListExit(key);
        continue;
      }
      const plan = dayFocusToTaskPlan(f);
      const currentMs = updatedAtMs(plan);
      updatedAtSnap.set(key, currentMs);

      const full = isTaskListFullyCompleted(plan);
      next.set(key, full);
      const prevVal = prev.get(key);
      const priorMs = prevTs.get(key) ?? currentMs;

      if (!full) {
        cancelWorkspaceListExit(key);
      } else if (prevVal === false) {
        frozenSortAnchorsRef.current[key] = priorMs;
        scheduleWorkspaceListExit(key);
      }
    }

    for (const key of prev.keys()) {
      if (!next.has(key)) {
        cancelWorkspaceListExit(key);
      }
    }

    prevWorkspaceFullyCompleteRef.current = next;
    prevUpdatedAtMsRef.current = updatedAtSnap;
  }, [
    allTasks,
    allFocuses,
    hideCompletedLists,
    cancelWorkspaceListExit,
    scheduleWorkspaceListExit,
  ]);

  useEffect(
    () => () => {
      for (const timers of Object.values(workspaceExitTimersRef.current)) {
        if (timers.hold) window.clearTimeout(timers.hold);
        if (timers.exit) window.clearTimeout(timers.exit);
      }
    },
    []
  );

  const workspaceSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /**
   * Tracks the currently-dragging element so the page-level `DragOverlay`
   * can render a visual ghost. Lifted here because action item drags now
   * register with the page's `DndContext` (see `externalDndContext` on
   * TaskCard and the action item drag namespace in
   * `src/utils/todoDragIds.ts`).
   */
  const [activeDrag, setActiveDrag] = useState<
    | { kind: 'card'; row: BoardRow }
    | { kind: 'action_item'; row: BoardRow; itemId: string }
    | { kind: 'bullet_top'; row: BoardRow }
    | { kind: 'bullet_child'; row: BoardRow; itemId: string }
    | null
  >(null);

  const visibleBoardRows = useMemo((): BoardRow[] => {
    const tasks = allTasks.filter((t) => {
      if (t.status === 'abandoned') return false;
      if (hideCompletedLists && isTaskListFullyCompleted(t)) return false;
      return true;
    });
    const focuses = allFocuses.filter((f) => {
      if (f.status === 'abandoned') return false;
      if (hideCompletedLists && isTaskListFullyCompleted(dayFocusToTaskPlan(f))) return false;
      return true;
    });

    const rows: BoardRow[] = [
      ...focuses.map((focus) => ({
        kind: 'day_focus' as const,
        id: focus.id,
        focus,
        task: dayFocusToTaskPlan(focus),
      })),
      ...tasks.map((task) => ({ kind: 'task' as const, id: task.id, task })),
    ];

    rows.sort((a, b) => updatedAtMs(b.task) - updatedAtMs(a.task));
    return rows;
  }, [allTasks, allFocuses, hideCompletedLists]);

  const boardRows = useMemo((): BoardRow[] => {
    const rows = [...visibleBoardRows];
    const seen = new Set(rows.map((r) => `${r.kind}:${r.id}`));

    if (hideCompletedLists) {
      for (const key of Object.keys(exitingWorkspaceCards)) {
        if (key.startsWith('task:')) {
          const id = key.slice('task:'.length);
          if (seen.has(`task:${id}`)) continue;
          const task = allTasks.find((t) => t.id === id);
          if (!task || task.status === 'abandoned') continue;
          if (!isTaskListFullyCompleted(task)) continue;
          rows.push({ kind: 'task', id, task });
          seen.add(`task:${id}`);
        } else if (key.startsWith('focus:')) {
          const id = key.slice('focus:'.length);
          if (seen.has(`day_focus:${id}`)) continue;
          const focus = allFocuses.find((f) => f.id === id);
          if (!focus || focus.status === 'abandoned') continue;
          const task = dayFocusToTaskPlan(focus);
          if (!isTaskListFullyCompleted(task)) continue;
          rows.push({ kind: 'day_focus', id, focus, task });
          seen.add(`day_focus:${id}`);
        }
      }
    }

    rows.sort(
      (a, b) =>
        boardRowSortActivityMs(b, frozenSortAnchorsRef.current) -
        boardRowSortActivityMs(a, frozenSortAnchorsRef.current),
    );
    return rows;
  }, [
    allTasks,
    allFocuses,
    exitingWorkspaceCards,
    hideCompletedLists,
    visibleBoardRows,
  ]);

  const laneForRow = useCallback(
    (row: BoardRow): TodoSwimLane =>
      row.kind === 'task'
        ? effectiveSwimLaneTaskPlan(row.task)
        : effectiveSwimLaneDayFocus(row.focus, todayKey),
    [todayKey]
  );

  const rowsByLaneActive = useMemo(() => {
    const map: Record<TodoSwimLane, BoardRow[]> = {
      today: [],
      this_week: [],
      later: [],
    };
    const now = new Date();
    for (const row of boardRows) {
      const lane = laneForRow(row);
      if (isLaterLaneArchivedByAge(row, lane, now)) continue;
      map[lane].push(row);
    }
    return map;
  }, [boardRows, laneForRow]);

  const laneActiveCounts = useMemo(() => {
    const counts: Record<TodoSwimLane, number> = { today: 0, this_week: 0, later: 0 };
    for (const lane of LANES) counts[lane] = rowsByLaneActive[lane].length;
    return counts;
  }, [rowsByLaneActive]);

  const rowsLaterArchived = useMemo(() => {
    const now = new Date();
    const archived: BoardRow[] = [];
    for (const row of boardRows) {
      const lane = laneForRow(row);
      if (isLaterLaneArchivedByAge(row, lane, now)) archived.push(row);
    }
    archived.sort((a, b) => {
      const tb = createdAtToDate(b.task)?.getTime() ?? 0;
      const ta = createdAtToDate(a.task)?.getTime() ?? 0;
      return tb - ta;
    });
    return archived;
  }, [boardRows, laneForRow]);

  /** Respect “Hide completed” inside Archive too; keep rows visible while genie-exit animation runs. */
  const rowsLaterArchivedVisible = useMemo(() => {
    if (!hideCompletedLists) return rowsLaterArchived;
    return rowsLaterArchived.filter((row) => {
      if (!isTaskListFullyCompleted(row.task)) return true;
      return Boolean(exitingWorkspaceCards[workspaceCardKey(row)]);
    });
  }, [rowsLaterArchived, hideCompletedLists, exitingWorkspaceCards]);

  const resolveRow = useCallback(
    (parsed: { kind: 'task' | 'day_focus'; id: string }): BoardRow | null => {
      const hit = boardRows.find((r) => r.kind === parsed.kind && r.id === parsed.id);
      return hit ?? null;
    },
    [boardRows]
  );

  const applyLaneAndMaybeColumn = useCallback(
    async (row: BoardRow, lane: TodoSwimLane, columnStatus: ActionItemStatus | null) => {
      const prevLane = laneForRow(row);
      if (prevLane !== lane) {
        if (row.kind === 'task') await setTaskSwimLane(row.task.id, lane);
        else await setFocusSwimLane(row.focus.id, lane);
      }

      if (taskViewMode === 'kanban' && columnStatus) {
        const task = row.task;
        const patch = statusesForWorkspaceColumn(task, columnStatus);
        if (Object.keys(patch).length === 0) return;
        const orderedIds = [...task.actionItems].sort((a, b) => a.order - b.order).map((i) => i.id);
        if (row.kind === 'task') {
          await reorder(row.task.id, orderedIds, patch);
        } else {
          await reorderFocusItems(row.focus.id, orderedIds, patch);
        }
      }
    },
    [
      laneForRow,
      reorder,
      reorderFocusItems,
      setFocusSwimLane,
      setTaskSwimLane,
      taskViewMode,
    ]
  );

  const handleWorkspaceDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const overId = event.over?.id ? String(event.over.id) : null;
      const rawActive = String(event.active.id);

      // Always clear the page-level drag overlay; individual branches below
      // are about *what* happens to the data, not the visual state.
      setActiveDrag(null);

      // ── Action item drag (page-level DndContext) ───────────────────────
      // TaskCard renders its action items into the page-level DndContext
      // when `externalDndContext={true}` (TodosPage Projects view). Drag
      // ids are namespaced as `ws-act:${kind}:${parentId}:${itemId}` so we
      // can route the drop back to the right document.
      if (isActionItemDragId(rawActive)) {
        const actionParsed = parseActionItemDragId(rawActive);
        if (!actionParsed || !overId) return;

        const parentRowKind: BoardRow['kind'] =
          actionParsed.parentKind === 'task' ? 'task' : 'day_focus';
        const parentRow = resolveRow({ kind: parentRowKind, id: actionParsed.parentId });
        if (!parentRow) return;

        const itemId = actionParsed.actionItemId;

        // ── Drop on another action item -> reorder (or cross-card move) ──
        const overParsed = parseActionItemDragId(overId);
        if (overParsed) {
          const sameParent =
            overParsed.parentKind === actionParsed.parentKind
            && overParsed.parentId === actionParsed.parentId;

          if (sameParent) {
            // In-card reorder. Replays the logic that used to live in
            // TaskCard.useTaskItemDnD.handleListDragEnd.
            const orderedIds = [...parentRow.task.actionItems]
              .sort((a, b) => a.order - b.order)
              .map((i) => i.id);
            const oldIndex = orderedIds.indexOf(itemId);
            const newIndex = orderedIds.indexOf(overParsed.actionItemId);
            if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
            const reordered = [...orderedIds];
            reordered.splice(oldIndex, 1);
            reordered.splice(newIndex, 0, itemId);
            if (parentRow.kind === 'task') await reorder(parentRow.task.id, reordered);
            else await reorderFocusItems(parentRow.focus.id, reordered);
            return;
          }

          // Cross-card action item move: extract from source, append to
          // destination as a new action item with the same text. Status
          // resets to 'todo' on move; this matches what users expect when
          // they drop a half-finished item into a different bucket.
          const item = parentRow.task.actionItems.find((i) => i.id === itemId);
          if (!item) return;
          const targetRowKind: BoardRow['kind'] =
            overParsed.parentKind === 'task' ? 'task' : 'day_focus';
          const targetRow = resolveRow({ kind: targetRowKind, id: overParsed.parentId });
          if (!targetRow) return;
          if (targetRow.kind === 'task') await addItem(targetRow.task.id, item.text);
          else await addFocusItem(targetRow.focus.id, item.text);
          if (parentRow.kind === 'task') await deleteItem(parentRow.task.id, itemId);
          else await deleteFocusItem(parentRow.focus.id, itemId);
          setToast({
            id: `action-cross-card-${Date.now()}`,
            message: `Moved to "${targetRow.task.title}"`,
            durationMs: 3500,
          });
          return;
        }

        // From here every "outer drop target" only updates the dragged
        // item's *status* / its parent's *lane* — never creates a new
        // TaskPlan. The Projects pivot's `ColumnsGrid` projects a single
        // mixed-status TaskPlan into every column where it has items, so
        // visually the card "appears" in the target column without any
        // duplication of Firestore documents.
        const item = parentRow.task.actionItems.find((i) => i.id === itemId);
        if (!item) return;

        // ── Drop on a project Kanban column ─────────────────────────────
        // Patch this one item's status, and re-assign the parent's project
        // if the column belongs to a different project. The card will
        // visibly appear in the target column thanks to the rowsByColumn
        // projection.
        const projColumn = parseProjectColumnDrop(overId);
        if (projColumn) {
          const prevProjectId = parentRow.task.projectId ?? null;
          if (prevProjectId !== projColumn.projectId) {
            if (parentRow.kind === 'task') await setTaskProject(parentRow.task.id, projColumn.projectId);
            else await setFocusProject(parentRow.focus.id, projColumn.projectId);
          }
          const orderedIds = [...parentRow.task.actionItems]
            .sort((a, b) => a.order - b.order)
            .map((i) => i.id);
          const patch: Partial<Record<string, ActionItemStatus>> = { [itemId]: projColumn.status };
          if (parentRow.kind === 'task') await reorder(parentRow.task.id, orderedIds, patch);
          else await reorderFocusItems(parentRow.focus.id, orderedIds, patch);
          return;
        }

        // ── Drop on a Schedule kanban cell ─────────────────────────────
        // Reschedule the parent into the target lane + flip *just this
        // item's* status. The kanban grid's per-cell projection takes care
        // of moving the card visibly into the new (lane × status) cell
        // while siblings stay in their current cells.
        const kanCell = parseKanbanCellDrop(overId);
        if (kanCell) {
          const prevLane = laneForRow(parentRow);
          if (prevLane !== kanCell.lane) {
            if (parentRow.kind === 'task') await setTaskSwimLane(parentRow.task.id, kanCell.lane);
            else await setFocusSwimLane(parentRow.focus.id, kanCell.lane);
          }
          const orderedIds = [...parentRow.task.actionItems]
            .sort((a, b) => a.order - b.order)
            .map((i) => i.id);
          const patch: Partial<Record<string, ActionItemStatus>> = { [itemId]: kanCell.status };
          if (parentRow.kind === 'task') await reorder(parentRow.task.id, orderedIds, patch);
          else await reorderFocusItems(parentRow.focus.id, orderedIds, patch);
          return;
        }

        // ── Drop on a Schedule lane chip ───────────────────────────────
        // Action items on lane chips reschedule the *parent* card. The
        // single dragged item itself doesn't have a lane; lanes are a
        // card-level property.
        const laneTarget = parseListLaneDrop(overId);
        if (laneTarget) {
          await applyLaneAndMaybeColumn(parentRow, laneTarget, null);
          return;
        }

        // ── Drop on Unschedule ─────────────────────────────────────────
        if (overId === 'ws-unschedule') {
          if (parentRow.kind === 'task') await setTaskSwimLane(parentRow.task.id, null);
          else await setFocusSwimLane(parentRow.focus.id, null);
          return;
        }

        // Drops on bullets indent / lane zones (`ws-bullet-*`) are not
        // sensible for action items dragged from a TaskCard — skip.
        return;
      }

      // ── Bullets outline drag handling ───────────────────────────────────
      // Bullets use their own drag-id prefix so card and bullet drag spaces
      // don't collide. We parse and dispatch first; if the drag isn't a
      // bullet drag we fall through to the card path below.
      const bulletActive = parseBulletDragId(rawActive);
      if (bulletActive) {
        if (!overId) return;
        const bulletRow = resolveRow({ kind: bulletActive.kind, id: bulletActive.rowId });
        if (!bulletRow) return;

        // Drop onto a lane heading -> reschedule.
        const laneTarget = parseBulletLaneDrop(overId);
        if (laneTarget) {
          if (!bulletActive.actionItemId) {
            // Top-level row -> change swimLane.
            const prev = laneForRow(bulletRow);
            if (prev === laneTarget) return;
            if (bulletRow.kind === 'task') await setTaskSwimLane(bulletRow.task.id, laneTarget);
            else await setFocusSwimLane(bulletRow.focus.id, laneTarget);
            setToast({
              id: `bullet-lane-${Date.now()}`,
              message: `Moved to ${TODO_LANE_LABELS[laneTarget]}`,
              durationMs: 3500,
            });
            return;
          }
          // Child item dragged to a different lane -> promote it to a new
          // top-level TaskPlan in the target lane and remove it from the
          // source. Cross-card moves are otherwise deferred per PRD.
          const item = bulletRow.task.actionItems.find((i) => i.id === bulletActive.actionItemId);
          if (!item) return;
          if (!user?.uid) return;
          const newId = await createBulletTask(item.text, { swimLane: laneTarget });
          if (newId) {
            if (bulletRow.kind === 'task') await deleteItem(bulletRow.task.id, item.id);
            else await deleteFocusItem(bulletRow.focus.id, item.id);
          }
          setToast({
            id: `bullet-promote-${Date.now()}`,
            message: `Moved to ${TODO_LANE_LABELS[laneTarget]}`,
            durationMs: 3500,
          });
          return;
        }

        // Drop onto an indent dropzone -> nest within the same row.
        const indentTarget = parseBulletIndentDrop(overId);
        if (indentTarget) {
          // Cross-card indenting is not supported yet.
          if (indentTarget.rowId !== bulletActive.rowId || indentTarget.rowKind !== bulletActive.kind) {
            return;
          }
          // Indenting a top-level row under one of its own items doesn't make sense.
          if (!bulletActive.actionItemId) return;
          // Don't allow indenting under self.
          if (indentTarget.parentItemId === bulletActive.actionItemId) return;
          if (bulletRow.kind === 'task') {
            await setTaskItemParent(
              bulletRow.task.id,
              bulletActive.actionItemId,
              indentTarget.parentItemId ?? null,
            );
          } else {
            await setFocusItemParent(
              bulletRow.focus.id,
              bulletActive.actionItemId,
              indentTarget.parentItemId ?? null,
            );
          }
          return;
        }

        // Bullets dragged onto card-drop targets (project pills, kanban cells)
        // are intentionally ignored for now to keep semantics clean.
        return;
      }

      const activeParsed = parseWorkspaceDragId(rawActive);
      if (!overId || !activeParsed) return;

      const row = resolveRow(activeParsed);
      if (!row) return;

      const prevLane = laneForRow(row);

      // Drop onto the Unschedule chip = clear `swimLane` (the card stops
      // appearing in any Schedule lane but stays in its project).
      if (overId === 'ws-unschedule') {
        if (row.task.swimLane === undefined && row.kind === 'task') return;
        if (row.kind === 'task') await setTaskSwimLane(row.task.id, null);
        else await setFocusSwimLane(row.focus.id, null);
        setToast({
          id: `unschedule-${Date.now()}`,
          message: `Removed from ${TODO_LANE_LABELS[prevLane]}`,
          durationMs: 3500,
        });
        return;
      }

      // Drop onto a Projects-pivot Kanban column = (re)assign project + flip
      // every action item to that column's status. Same drop = both effects,
      // so dragging "to do" → "doing" inside a project Just Works.
      const projColumn = parseProjectColumnDrop(overId);
      if (projColumn) {
        const prevProjectId = row.task.projectId ?? null;
        if (prevProjectId !== projColumn.projectId) {
          if (row.kind === 'task') await setTaskProject(row.task.id, projColumn.projectId);
          else await setFocusProject(row.focus.id, projColumn.projectId);
        }
        const task = row.task;
        const patch = statusesForWorkspaceColumn(task, projColumn.status);
        if (Object.keys(patch).length > 0) {
          const orderedIds = [...task.actionItems]
            .sort((a, b) => a.order - b.order)
            .map((i) => i.id);
          if (row.kind === 'task') await reorder(row.task.id, orderedIds, patch);
          else await reorderFocusItems(row.focus.id, orderedIds, patch);
        }
        return;
      }

      // Drop onto a project-switcher pill = (re)assign that card's project
      // (or clear it for the Inbox pill). Cards keep their lane so this is
      // a pure project move — symmetrical to ScheduleDropZones' lane-only
      // semantics.
      const pillTarget = parseProjectPillDrop(overId);
      if (pillTarget !== undefined) {
        const prevProjectId = row.task.projectId ?? null;
        if (prevProjectId === pillTarget) return;
        if (row.kind === 'task') await setTaskProject(row.task.id, pillTarget);
        else await setFocusProject(row.focus.id, pillTarget);
        const targetLabel = pillTarget
          ? projectsById.get(pillTarget)?.name ?? 'project'
          : 'Inbox';
        setToast({
          id: `assign-project-${Date.now()}`,
          message: `Moved to ${targetLabel}`,
          durationMs: 3500,
        });
        return;
      }

      const listLane = parseListLaneDrop(overId);
      if (listLane) {
        // Lane drop targets are now valid in any view + grouping. The
        // ScheduleDropZones above the Projects pivot reuse this same drop ID
        // (`ws-list-lane:${lane}`) so dragging a project card onto Today /
        // This week / Later schedules it without touching `projectId`.
        await applyLaneAndMaybeColumn(row, listLane, null);
        if (grouping === 'project' && prevLane !== listLane) {
          const projectName = row.task.projectId
            ? projectsById.get(row.task.projectId)?.name ?? null
            : null;
          setToast({
            id: `schedule-from-project-${Date.now()}`,
            message: projectName
              ? `Scheduled for ${TODO_LANE_LABELS[listLane]} — still in ${projectName}`
              : `Scheduled for ${TODO_LANE_LABELS[listLane]} — still in Inbox`,
            durationMs: 3500,
          });
        }
        return;
      }

      const kanCell = parseKanbanCellDrop(overId);
      if (kanCell) {
        await applyLaneAndMaybeColumn(row, kanCell.lane, kanCell.status);
      }
    },
    [
      addFocusItem,
      addItem,
      applyLaneAndMaybeColumn,
      createBulletTask,
      deleteFocusItem,
      deleteItem,
      grouping,
      laneForRow,
      projectsById,
      reorder,
      reorderFocusItems,
      resolveRow,
      setFocusItemParent,
      setFocusProject,
      setFocusSwimLane,
      setTaskItemParent,
      setTaskProject,
      setTaskSwimLane,
      user?.uid,
    ]
  );

  /**
   * `onDragStart` for the page-level DndContext — captures who's being
   * dragged so the `<DragOverlay>` further down can render a visual ghost.
   * `onDragCancel` and `onDragEnd` both clear via `setActiveDrag(null)`.
   */
  const handleWorkspaceDragStart = useCallback(
    (event: DragStartEvent) => {
      const raw = String(event.active.id);

      const actionParsed = parseActionItemDragId(raw);
      if (actionParsed) {
        const row = resolveRow({
          kind: actionParsed.parentKind === 'task' ? 'task' : 'day_focus',
          id: actionParsed.parentId,
        });
        if (row) setActiveDrag({ kind: 'action_item', row, itemId: actionParsed.actionItemId });
        return;
      }

      const bulletParsed = parseBulletDragId(raw);
      if (bulletParsed) {
        const row = resolveRow({ kind: bulletParsed.kind, id: bulletParsed.rowId });
        if (row) {
          setActiveDrag(
            bulletParsed.actionItemId
              ? { kind: 'bullet_child', row, itemId: bulletParsed.actionItemId }
              : { kind: 'bullet_top', row },
          );
        }
        return;
      }

      const cardParsed = parseWorkspaceDragId(raw);
      if (cardParsed) {
        const row = resolveRow(cardParsed);
        if (row) setActiveDrag({ kind: 'card', row });
      }
    },
    [resolveRow],
  );

  const handleWorkspaceDragCancel = useCallback(() => {
    setActiveDrag(null);
  }, []);

  const loading = tasksLoading || focusLoading || projectsLoading;

  // ─── Project pivot rows + cascade plumbing ─────────────────────────────────

  const visibleProjectRows = useMemo((): ProjectsPivotRow[] => {
    const rows: ProjectsPivotRow[] = [];
    for (const focus of allFocuses) {
      if (focus.status === 'abandoned') continue;
      if (hideCompletedLists && isFocusFullyCompleted(focus)) continue;
      rows.push({
        kind: 'day_focus',
        id: focus.id,
        focus,
        task: dayFocusToTaskPlan(focus),
      });
    }
    for (const t of allTasks) {
      if (t.status === 'abandoned') continue;
      if (hideCompletedLists && isTaskListFullyCompleted(t)) continue;
      rows.push({ kind: 'task', id: t.id, task: t });
    }
    rows.sort((a, b) => updatedAtMs(b.task) - updatedAtMs(a.task));
    return rows;
  }, [allTasks, allFocuses, hideCompletedLists]);

  /**
   * Cards currently scheduled in each lane, surfaced as draggable mini-chips
   * inside `ScheduleDropZones`. Only includes cards with an explicit
   * `swimLane` (so we don't pretend defaults are "scheduled") and respects
   * the Hide-completed toggle so the chips stay tidy.
   */
  const projectViewRowsByLane = useMemo(() => {
    const map: Record<TodoSwimLane, ProjectsPivotRow[]> = {
      today: [],
      this_week: [],
      later: [],
    };
    for (const row of visibleProjectRows) {
      const lane = row.task.swimLane;
      if (!lane) continue;
      map[lane].push(row);
    }
    return map;
  }, [visibleProjectRows]);

  /**
   * Counts mirror `projectViewRowsByLane`. Could be derived from the map but
   * kept separate so the zone count chips don't lie if the map ever filters
   * differently (e.g. only show the user's pinned cards in chips later).
   */
  const projectViewLaneCounts = useMemo<Record<TodoSwimLane, number>>(() => ({
    today: projectViewRowsByLane.today.length,
    this_week: projectViewRowsByLane.this_week.length,
    later: projectViewRowsByLane.later.length,
  }), [projectViewRowsByLane]);

  /**
   * Active (= not abandoned, not fully completed) cards that have no project
   * assigned. Surfaced as a small badge on the Inbox pill so users can see
   * fresh captures from morning check-ins / sessions at a glance.
   */
  const inboxActiveCount = useMemo(() => {
    let count = 0;
    for (const t of allTasks) {
      if (t.status === 'abandoned') continue;
      if (isTaskListFullyCompleted(t)) continue;
      if (!t.projectId) count++;
    }
    for (const f of allFocuses) {
      if (f.status === 'abandoned') continue;
      if (isFocusFullyCompleted(f)) continue;
      if (!f.projectId) count++;
    }
    return count;
  }, [allTasks, allFocuses]);

  const setRowProject = useCallback(
    async (row: ProjectsPivotRow, projectId: string | null) => {
      if (row.kind === 'task') await setTaskProject(row.task.id, projectId);
      else await setFocusProject(row.focus.id, projectId);
    },
    [setTaskProject, setFocusProject]
  );

  const requestCascade = useCallback(
    (projectId: string, lane: TodoSwimLane) => {
      setCascadeProjectId(projectId);
      setCascadeTargetLane(lane);
    },
    []
  );

  // ─── Project switcher: validate persisted key against live projects ────────

  const sortedActiveProjects = useMemo(
    () => sortProjectsForPivot(projects, { includeArchived: false }),
    [projects]
  );

  useEffect(() => {
    if (selectedProjectKey === null) return;
    if (selectedProjectKey === PROJECT_SWITCHER_INBOX_KEY) return;
    if (projectsLoading) return;
    const exists = projects.some((p) => p.id === selectedProjectKey);
    if (!exists) setSelectedProjectKey(null);
  }, [selectedProjectKey, projects, projectsLoading, setSelectedProjectKey]);

  // ─── Bulk archive: every active TaskPlan + DayFocus across all lanes ───────

  const bulkArchiveCounts = useMemo(() => {
    let tasks = 0;
    let focuses = 0;
    for (const t of allTasks) {
      if (t.status !== 'abandoned') tasks++;
    }
    for (const f of allFocuses) {
      if (f.status !== 'abandoned') focuses++;
    }
    return { tasks, focuses };
  }, [allTasks, allFocuses]);

  const runBulkArchive = useCallback(async () => {
    if (!user?.uid) return;
    setBulkArchiveBusy(true);
    try {
      // Snapshot prior statuses for undo.
      const taskRestore: Array<{ id: string; status: 'active' | 'completed' }> = [];
      const focusRestore: Array<{ id: string; status: 'active' | 'completed' }> = [];

      for (const t of allTasks) {
        if (t.status === 'abandoned') continue;
        taskRestore.push({ id: t.id, status: t.status });
      }
      for (const f of allFocuses) {
        if (f.status === 'abandoned') continue;
        focusRestore.push({ id: f.id, status: f.status });
      }

      const total = taskRestore.length + focusRestore.length;
      if (total === 0) {
        setBulkArchiveBusy(false);
        setBulkArchiveOpen(false);
        setToast({
          id: `bulk-archive-empty-${Date.now()}`,
          message: 'Nothing to archive — every list is already retired.',
          durationMs: 3500,
        });
        return;
      }

      const uid = user.uid;
      await Promise.all([
        ...taskRestore.map((t) => updateTaskStatus(uid, t.id, 'abandoned')),
        ...focusRestore.map((f) => updateDayFocusStatus(uid, f.id, 'abandoned')),
      ]);

      setBulkArchiveBusy(false);
      setBulkArchiveOpen(false);
      setToast({
        id: `bulk-archive-${Date.now()}`,
        message: `Archived ${total} list${total === 1 ? '' : 's'}. New ones will arrive in Inbox.`,
        durationMs: 8000,
        onUndo: async () => {
          await Promise.all([
            ...taskRestore.map((t) => updateTaskStatus(uid, t.id, t.status)),
            ...focusRestore.map((f) => updateDayFocusStatus(uid, f.id, f.status)),
          ]);
        },
      });
    } catch (err) {
      setBulkArchiveBusy(false);
      setBulkArchiveOpen(false);
      setToast({
        id: `bulk-archive-error-${Date.now()}`,
        message:
          err instanceof Error && err.message
            ? `Couldn't archive lists: ${err.message}`
            : 'Bulk archive failed. Please try again.',
        durationMs: 5000,
      });
    }
  }, [user?.uid, allTasks, allFocuses]);

  /**
   * Confirm callback for the Flank-styled CreateProjectSheet. Validates the
   * name, calls the existing `createProject` hook (which preserves provenance
   * + optional color), auto-selects the new project in the switcher, and
   * surfaces failures via the existing toast.
   */
  const confirmCreateProject = useCallback(
    async (name: string, colorKey?: TodoProjectColorKey) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      setCreateSheetBusy(true);
      try {
        const id = await createProject(trimmed, colorKey);
        setCreateSheetBusy(false);
        setCreateSheetOpen(false);
        if (id) {
          setSelectedProjectKey(id);
          setToast({
            id: `created-${Date.now()}`,
            message: `Project "${trimmed}" created.`,
            durationMs: 3500,
          });
        }
      } catch (err) {
        setCreateSheetBusy(false);
        setToast({
          id: `create-error-${Date.now()}`,
          message:
            err instanceof Error && err.message
              ? `Couldn't create project: ${err.message}`
              : "Couldn't create project. Please try again.",
          durationMs: 5000,
        });
      }
    },
    [createProject, setSelectedProjectKey]
  );

  const cascadeProject = useMemo(
    () => (cascadeProjectId ? projectsById.get(cascadeProjectId) ?? null : null),
    [cascadeProjectId, projectsById]
  );

  const cascadeAffectedSummary = useMemo(() => {
    if (!cascadeProjectId) return { active: 0, completed: 0 };
    let active = 0;
    let completed = 0;
    for (const t of allTasks) {
      if (t.projectId !== cascadeProjectId) continue;
      if (isTaskListFullyCompleted(t)) completed++;
      else active++;
    }
    for (const f of allFocuses) {
      if (f.projectId !== cascadeProjectId) continue;
      if (isFocusFullyCompleted(f)) completed++;
      else active++;
    }
    return { active, completed };
  }, [cascadeProjectId, allTasks, allFocuses]);

  const runCascade = useCallback(async () => {
    if (!user?.uid || !cascadeProjectId) return;
    setCascadeBusy(true);
    try {
      const projectName = cascadeProject?.name ?? 'project';
      const affected = await cascadeProjectSwimLane(
        user.uid,
        cascadeProjectId,
        cascadeTargetLane,
        { tasks: allTasks, focuses: allFocuses }
      );
      setCascadeBusy(false);
      setCascadeProjectId(null);
      const laneLabel = TODO_LANE_LABELS[cascadeTargetLane];
      if (affected.length === 0) {
        setToast({
          id: `cascade-empty-${Date.now()}`,
          message: `No active lists in ${projectName} needed updating.`,
          durationMs: 3500,
        });
        return;
      }
      const undoBatch: CascadeAffectedCard[] = affected;
      setToast({
        id: `cascade-${Date.now()}`,
        message: `Moved ${affected.length} list${affected.length === 1 ? '' : 's'} to ${laneLabel}`,
        durationMs: 6000,
        onUndo: async () => {
          if (!user?.uid) return;
          await restoreCardSwimLanes(user.uid, undoBatch);
        },
      });
    } catch (err) {
      setCascadeBusy(false);
      setCascadeProjectId(null);
      setToast({
        id: `cascade-error-${Date.now()}`,
        message:
          err instanceof Error && err.message
            ? `Couldn't update project: ${err.message}`
            : 'Cascade failed. Please try again.',
        durationMs: 5000,
      });
    }
  }, [user?.uid, cascadeProjectId, cascadeProject, cascadeTargetLane, allTasks, allFocuses]);

  // ─── Project delete flow ──────────────────────────────────────────────────

  const requestDeleteProject = useCallback(
    async (projectId: string) => {
      setPendingDeleteProjectId(projectId);
      setPendingDeleteCounts(null);
      const counts = await countReferencingCards(projectId);
      setPendingDeleteCounts(counts);
    },
    [countReferencingCards]
  );

  const confirmDeleteProject = useCallback(async () => {
    if (!pendingDeleteProjectId) return;
    setDeleteBusy(true);
    try {
      const counts = pendingDeleteCounts ?? (await countReferencingCards(pendingDeleteProjectId));
      if (counts.tasks + counts.focuses > 0) {
        await moveAllToInbox(pendingDeleteProjectId);
      }
      await deleteProject(pendingDeleteProjectId);
      setToast({
        id: `delete-${Date.now()}`,
        message: 'Project deleted. Lists moved to Inbox.',
        durationMs: 4500,
      });
    } catch (err) {
      setToast({
        id: `delete-error-${Date.now()}`,
        message:
          err instanceof Error && err.message
            ? `Couldn't delete project: ${err.message}`
            : 'Delete failed. Please try again.',
        durationMs: 5000,
      });
    } finally {
      setDeleteBusy(false);
      setPendingDeleteProjectId(null);
      setPendingDeleteCounts(null);
    }
  }, [pendingDeleteProjectId, pendingDeleteCounts, countReferencingCards, moveAllToInbox, deleteProject]);

  // ─── Top-bar overflow menu (replaces the old secondary action row) ────────
  // Schedule mode: just Show/Hide completed lives in the menu — the rest is on
  // the toolbar. Project mode also exposes the bulk archive flow which is too
  // destructive to live as a constantly-visible button.
  const topBarMenuItems = useMemo<TopBarMenuItem[]>(() => {
    const items: TopBarMenuItem[] = [];
    if (grouping === 'project') {
      items.push({
        id: 'archive-all',
        label: 'Archive all current lists',
        icon: <Archive size={14} />,
        destructive: true,
        onSelect: () => setBulkArchiveOpen(true),
      });
    }
    items.push({
      id: 'toggle-completed',
      label: hideCompletedLists ? 'Show completed lists' : 'Hide completed lists',
      icon: hideCompletedLists ? <Eye size={14} /> : <EyeOff size={14} />,
      onSelect: () => setHideCompletedLists((prev) => !prev),
    });
    return items;
  }, [grouping, hideCompletedLists]);

  // ─── Top toolbar: cycle between List → Kanban → Bullets ───────────────────
  // 'bullets' is a Workflowy-style outline projection over the same
  // TaskPlan/DayFocus + actionItems data the other two views use.
  const cycleViewMode = useCallback(() => {
    const order: TaskCardViewMode[] = ['list', 'kanban', 'bullets'];
    const idx = order.indexOf(taskViewMode);
    const next = order[(idx + 1) % order.length];
    setTaskViewMode(next);
  }, [taskViewMode, setTaskViewMode]);

  const viewModeMeta: Record<TaskCardViewMode, { icon: ReactNode; label: string; nextLabel: string }> = {
    list: {
      icon: <List size={18} />,
      label: 'List view',
      nextLabel: 'Switch to Kanban',
    },
    kanban: {
      icon: <Columns3 size={18} />,
      label: 'Kanban view',
      nextLabel: 'Switch to Bullets',
    },
    bullets: {
      icon: <ListTree size={18} />,
      label: 'Bullets view',
      nextLabel: 'Switch to List',
    },
  };

  const groupingPill = (
    <div
      role="tablist"
      aria-label="Grouping"
      className="inline-flex items-center rounded-xl border border-[var(--color-border)] p-0.5"
      style={{ background: 'var(--color-surface)' }}
    >
      <button
        type="button"
        role="tab"
        aria-selected={grouping === 'schedule'}
        onClick={() => setGrouping('schedule')}
        title="Group by schedule"
        className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
          grouping === 'schedule'
            ? 'text-[var(--color-text-primary)]'
            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
        }`}
        style={
          grouping === 'schedule'
            ? { background: 'var(--color-surface-elevated)', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }
            : undefined
        }
      >
        <CalendarClock size={14} />
        <span className="hidden sm:inline">Schedule</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={grouping === 'project'}
        onClick={() => setGrouping('project')}
        title="Group by project"
        className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
          grouping === 'project'
            ? 'text-[var(--color-text-primary)]'
            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
        }`}
        style={
          grouping === 'project'
            ? { background: 'var(--color-surface-elevated)', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }
            : undefined
        }
      >
        <FolderTree size={14} />
        <span className="hidden sm:inline">Projects</span>
      </button>
    </div>
  );

  // The full right-side cluster: grouping pill + view cycle + completed toggle
  // + (Projects only) new-project + ellipsis menu. Replaces the old secondary
  // action row entirely so the Todos page only has one toolbar.
  const topBarActions = (
    <div className="flex items-center gap-1 shrink-0">
      {groupingPill}

      <button
        type="button"
        onClick={cycleViewMode}
        className="p-2 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
        title={viewModeMeta[taskViewMode].nextLabel}
        aria-label={viewModeMeta[taskViewMode].nextLabel}
      >
        {viewModeMeta[taskViewMode].icon}
      </button>

      {taskViewMode === 'kanban' && grouping === 'schedule' ? (
        <button
          type="button"
          onClick={() =>
            setKanbanOrientation(kanbanOrientation === 'horizontal' ? 'vertical' : 'horizontal')
          }
          className="p-2 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
          title={kanbanOrientation === 'horizontal' ? 'Vertical columns' : 'Horizontal columns'}
        >
          {kanbanOrientation === 'horizontal' ? <Rows3 size={18} /> : <Columns3 size={18} />}
        </button>
      ) : null}

      <button
        type="button"
        onClick={() => setHideCompletedLists((prev) => !prev)}
        aria-pressed={!hideCompletedLists}
        className="p-2 rounded-xl transition-colors"
        title={hideCompletedLists ? 'Show completed lists' : 'Hide completed lists'}
        style={{
          color: hideCompletedLists ? 'var(--color-text-secondary)' : brandColors.lightBlue,
          background: hideCompletedLists ? 'transparent' : `${brandColors.lightBlue}1A`,
        }}
      >
        {hideCompletedLists ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>

      {grouping === 'project' ? (
        <button
          type="button"
          onClick={() => setCreateSheetOpen(true)}
          className="p-2 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
          title="New project"
          aria-label="New project"
        >
          <FolderPlus size={18} />
        </button>
      ) : null}

      <TopBarMenu items={topBarMenuItems} />
    </div>
  );

  // ─── Shared card renderer ────────────────────────────────────────────────

  const renderProjectChip = useCallback(
    (row: BoardRow | ProjectsPivotRow) => {
      const projectId = row.task.projectId;
      const current = projectId ? projectsById.get(projectId) ?? null : null;
      return (
        <ProjectAssignButton
          projects={projects}
          current={current}
          compact
          onAssign={(nextId) => setRowProject(row as ProjectsPivotRow, nextId)}
          onCreate={(name, colorKey) => createProject(name, colorKey)}
        />
      );
    },
    [projects, projectsById, createProject, setRowProject]
  );

  const renderCardWithChip = useCallback(
    (
      row: BoardRow | ProjectsPivotRow,
      options?: {
        forceActionListLayout?: boolean;
        suppressViewToggleButtons?: boolean;
        showDateBadge?: boolean;
        badgeLabel?: string;
        /**
         * When set, render only these action items inside the card (used
         * by the Projects pivot's column projection so the same TaskPlan
         * appears in every column it has items in, with each cell showing
         * just its slice of the items).
         */
        visibleItemIds?: ReadonlySet<string>;
      }
    ) => {
      const dragId =
        row.kind === 'task'
          ? `ws-drag:task:${row.task.id}`
          : `ws-drag:focus:${(row as { focus: DayFocus }).focus.id}`;
      const exitPhase = exitingWorkspaceCards[workspaceCardKey(row as BoardRow)];
      return (
        <div className={workspaceRowExitShellClass(exitPhase)}>
          <WorkspaceDraggableShell dragId={dragId}>
            <div className="flex flex-col gap-1.5 min-w-0">
              <TaskCard
                task={row.task}
                onToggle={row.kind === 'task' ? toggle : toggleFocusItem}
                onAddItem={row.kind === 'task' ? addItem : addFocusItem}
                onDeleteItem={row.kind === 'task' ? deleteItem : deleteFocusItem}
                onReorder={row.kind === 'task' ? reorder : reorderFocusItems}
                variant={row.kind === 'day_focus' ? 'dayFocus' : 'task'}
                dateBadge={options?.showDateBadge ? dateBadgeFromCreated(row.task) : undefined}
                badgeLabel={options?.badgeLabel}
                forceActionListLayout={options?.forceActionListLayout}
                suppressViewToggleButtons={options?.suppressViewToggleButtons}
                visibleItemIds={options?.visibleItemIds}
                /* Use the page-level DndContext so action items inside this
                   card can be dragged across cards / onto Schedule chips /
                   onto project Kanban columns. The page handler dispatches
                   on the `ws-act:` drag id namespace defined in
                   `src/utils/todoDragIds.ts`. */
                externalDndContext
                projects={projects}
                currentProject={row.task.projectId ? projectsById.get(row.task.projectId) ?? null : null}
                onAssignProject={(nextId) => setRowProject(row as ProjectsPivotRow, nextId)}
                onCreateProject={(name, colorKey) => createProject(name, colorKey)}
              />
              <div className="flex items-center gap-1.5 pl-1">
                {renderProjectChip(row)}
              </div>
            </div>
          </WorkspaceDraggableShell>
        </div>
      );
    },
    [
      addFocusItem,
      addItem,
      createProject,
      deleteFocusItem,
      deleteItem,
      exitingWorkspaceCards,
      projects,
      projectsById,
      reorder,
      reorderFocusItems,
      renderProjectChip,
      setRowProject,
      toggle,
      toggleFocusItem,
    ]
  );

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <div
        className="sticky top-0 z-40"
        style={{
          background: 'var(--color-background)',
          borderBottom: '1px solid var(--color-border)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        <header className="px-5 lg:px-8 xl:px-12 pt-5 pb-3 max-w-[1600px] mx-auto flex flex-wrap items-center gap-3 relative">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="p-2 -ml-2 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors shrink-0"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">Todos</h1>
            <p className="text-xs text-[var(--color-text-muted)] truncate">
              {grouping === 'schedule'
                ? taskViewMode === 'bullets'
                  ? 'Outline — Tab/Shift+Tab to nest, Enter for next bullet, drag to reschedule.'
                  : 'Swim lanes + workflow columns stay synced across devices.'
                : 'Group lists by project. Cards stay in sync with the Schedule view.'}
            </p>
          </div>
          {topBarActions}
        </header>
      </div>

      <div className="px-5 lg:px-8 xl:px-12 py-5 max-w-[1600px] mx-auto flex flex-col gap-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-[var(--color-text-muted)]" size={28} />
          </div>
        ) : (
          <DndContext
            sensors={workspaceSensors}
            onDragStart={handleWorkspaceDragStart}
            onDragCancel={handleWorkspaceDragCancel}
            onDragEnd={handleWorkspaceDragEnd}
          >
            {grouping === 'project' ? (
              // ProjectSwitcher needs the DndContext for the new
              // drop-onto-pill (assign project) feature, so it lives inside
              // the provider rather than as a sibling above.
              <ProjectSwitcher
                projects={sortedActiveProjects}
                selectedKey={selectedProjectKey}
                onSelect={setSelectedProjectKey}
                inboxCount={inboxActiveCount}
              />
            ) : null}
            {grouping === 'schedule' ? (
              <ScheduleDateStrip
                onJumpToLane={jumpToLane}
                onSelectDate={handleSelectScheduleDate}
              />
            ) : (
              // ScheduleDropZones lives **inside** the DndContext so its
              // useDroppable / useDndMonitor / useDraggable calls find the
              // right provider. Pass the project-view lane data so chips
              // reflect what's actually scheduled (vs the Schedule-view
              // counts which include all visible rows regardless of project).
              <ScheduleDropZones
                laneCounts={projectViewLaneCounts}
                rowsByLane={projectViewRowsByLane}
                projectsById={projectsById}
              />
            )}
            {grouping === 'project' ? (
              <ProjectsPivot
                projects={projects}
                rows={visibleProjectRows}
                selectedProjectKey={selectedProjectKey}
                onCreateProject={() => setCreateSheetOpen(true)}
                onRenameProject={async (projectId, nextName) => {
                  try {
                    await renameProject(projectId, nextName);
                  } catch (err) {
                    setToast({
                      id: `rename-error-${Date.now()}`,
                      message:
                        err instanceof Error && err.message
                          ? `Couldn't rename: ${err.message}`
                          : "Couldn't rename project. Please try again.",
                      durationMs: 5000,
                    });
                  }
                }}
                renderProjectMenu={(project, _summary, { enterRenameMode }) => (
                  <ProjectHeaderMenu
                    isPinned={Boolean(project.pinned)}
                    isArchived={Boolean(project.archived)}
                    onCascade={(lane) => requestCascade(project.id, lane)}
                    onTogglePin={() => setProjectPinned(project.id, !project.pinned)}
                    onArchive={() => setProjectArchived(project.id, true)}
                    onUnarchive={() => setProjectArchived(project.id, false)}
                    onRenameRequested={enterRenameMode}
                    onDeleteRequested={() => requestDeleteProject(project.id)}
                  />
                )}
                renderCard={(row, opts) => renderCardWithChip(row, {
                  forceActionListLayout: true,
                  suppressViewToggleButtons: true,
                  // The "Mixed steps" badge no longer applies once the
                  // pivot projects mixed-status cards into separate
                  // columns — each visible card slice is single-status.
                  badgeLabel: undefined,
                  visibleItemIds: opts?.visibleItemIds,
                })}
              />
            ) : selectedPastDate !== null ? (
              // Past-day retrospective view (Schedule grouping). Hides the
              // Today / This week / Later lanes — those don't fit the past
              // day mental model. The strip stays interactive so the user
              // can scrub back to today.
              <PastDaySection
                date={selectedPastDate}
                tasks={allTasks}
                focuses={allFocuses}
              />
            ) : taskViewMode === 'bullets' ? (
              <BulletsCanvas
                rows={visibleBoardRows as BulletsRow[]}
                laneForRow={(row) => laneForRow(row as BoardRow)}
                projects={projects}
                onToggleItem={(row, itemId, completed) =>
                  row.kind === 'task'
                    ? toggle(row.task.id, itemId, completed)
                    : toggleFocusItem(row.focus.id, itemId, completed)
                }
                onAddItem={async (row, text, options) =>
                  row.kind === 'task'
                    ? (await addItem(row.task.id, text, options)) ?? null
                    : (await addFocusItem(row.focus.id, text, options)) ?? null
                }
                onDeleteItem={(row, itemId) =>
                  row.kind === 'task'
                    ? deleteItem(row.task.id, itemId)
                    : deleteFocusItem(row.focus.id, itemId)
                }
                onRenameItem={(row, itemId, text) =>
                  row.kind === 'task'
                    ? renameTaskItem(row.task.id, itemId, text)
                    : renameFocusItem(row.focus.id, itemId, text)
                }
                onSetItemParent={(row, itemId, parentItemId) =>
                  row.kind === 'task'
                    ? setTaskItemParent(row.task.id, itemId, parentItemId)
                    : setFocusItemParent(row.focus.id, itemId, parentItemId)
                }
                onRenameRow={(row, title) =>
                  row.kind === 'task'
                    ? renameTask(row.task.id, title)
                    : renameFocus(row.focus.id, title)
                }
                onDeleteRow={(row) =>
                  row.kind === 'task' ? deleteTask(row.task.id) : deleteFocus(row.focus.id)
                }
                onCreateTopLevel={async (lane, text) => createBulletTask(text, { swimLane: lane })}
                onMoveRowToLane={(row, lane) =>
                  row.kind === 'task'
                    ? setTaskSwimLane(row.task.id, lane)
                    : setFocusSwimLane(row.focus.id, lane)
                }
                laneCollapsed={laneCollapsed}
                toggleLaneCollapsed={toggleLaneCollapsed}
              />
            ) : taskViewMode === 'list' ? (
              <div className="flex flex-col gap-8">
                {TODO_SWIM_LANE_META.map((laneMeta) => {
                  const collapsed = laneCollapsed[laneMeta.id];
                  const activeCount = laneActiveCounts[laneMeta.id];
                  return (
                  <LaneDropSurface key={laneMeta.id} dropId={`ws-list-lane:${laneMeta.id}`}>
                    <div
                      id={`ws-lane-${laneMeta.id}`}
                      className="flex flex-col gap-2 scroll-mt-24"
                    >
                      <button
                        type="button"
                        onClick={() => toggleLaneCollapsed(laneMeta.id)}
                        className="flex items-center gap-2 text-left rounded-lg p-1 -ml-1 hover:bg-[var(--color-surface)] transition-colors"
                        aria-expanded={!collapsed}
                        aria-controls={`ws-list-lane-body-${laneMeta.id}`}
                      >
                        {collapsed ? (
                          <ChevronRight size={16} className="text-[var(--color-text-muted)]" />
                        ) : (
                          <ChevronDown size={16} className="text-[var(--color-text-muted)]" />
                        )}
                        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                          {laneMeta.label}
                        </h2>
                        <span className="text-[11px] text-[var(--color-text-muted)]">
                          {activeCount === 0 ? 'nothing planned' : `${activeCount} list${activeCount === 1 ? '' : 's'}`}
                        </span>
                      </button>
                      {!collapsed ? (
                        <p className="text-[11px] text-[var(--color-text-muted)] pl-7">
                          {laneMeta.hint}
                          {laneMeta.id === 'later'
                            ? ` Each list shows when it was created. After ${LATER_ARCHIVE_AFTER_DAYS} days they move to Archive (hidden until you open it).`
                            : ''}
                        </p>
                      ) : null}
                      {!collapsed ? (
                      <div id={`ws-list-lane-body-${laneMeta.id}`} className="flex flex-col gap-3">
                        {laneMeta.id === 'later' ? (
                          <>
                            {rowsByLaneActive.later.length === 0 && rowsLaterArchived.length === 0 ? (
                              <p className="text-sm text-[var(--color-text-muted)] py-2">
                                Nothing here — drag cards between lanes below or add tasks from Home or sessions.
                              </p>
                            ) : (
                              <>
                                {rowsByLaneActive.later.length === 0 && rowsLaterArchived.length > 0 && !showLaterArchive ? (
                                  <p className="text-sm text-[var(--color-text-muted)] py-2">
                                    Nothing recent in Later. Open Archive for lists from more than{' '}
                                    {LATER_ARCHIVE_AFTER_DAYS} days ago.
                                  </p>
                                ) : null}
                                {rowsByLaneActive.later.map((row) => {
                                  const dragId =
                                    row.kind === 'task'
                                      ? `ws-drag:task:${row.task.id}`
                                      : `ws-drag:focus:${row.focus.id}`;
                                  const mixed = taskHasMixedOpenStatuses(row.task);
                                  const col = workspaceKanbanColumnForTask(row.task);
                                  const exitPhase = exitingWorkspaceCards[workspaceCardKey(row)];
                                  return (
                                    <div key={dragId} className={workspaceRowExitShellClass(exitPhase)}>
                                      <WorkspaceDraggableShell dragId={dragId}>
                                        <TaskCard
                                          task={row.task}
                                          onToggle={row.kind === 'task' ? toggle : toggleFocusItem}
                                          onAddItem={row.kind === 'task' ? addItem : addFocusItem}
                                          onDeleteItem={row.kind === 'task' ? deleteItem : deleteFocusItem}
                                          onReorder={row.kind === 'task' ? reorder : reorderFocusItems}
                                          variant={row.kind === 'day_focus' ? 'dayFocus' : 'task'}
                                          dateBadge={dateBadgeFromCreated(row.task)}
                                          badgeLabel={
                                            mixed ? `Mixed · ${WS_COLUMNS.find((c) => c.id === col)?.label ?? col}` : undefined
                                          }
                                          externalDndContext
                                        />
                                      </WorkspaceDraggableShell>
                                    </div>
                                  );
                                })}
                                {rowsLaterArchived.length > 0 ? (
                                  <div className="mt-1 pt-3 border-t border-dashed border-[var(--color-border)]">
                                    <button
                                      type="button"
                                      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-left text-sm font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
                                      onClick={() => setShowLaterArchive((open) => !open)}
                                    >
                                      {showLaterArchive ? (
                                        <ChevronDown size={18} className="shrink-0" />
                                      ) : (
                                        <ChevronRight size={18} className="shrink-0" />
                                      )}
                                      <span>
                                        Archive ({rowsLaterArchivedVisible.length})
                                      </span>
                                      <span className="text-xs font-normal text-[var(--color-text-muted)]">
                                        Created over {LATER_ARCHIVE_AFTER_DAYS} days ago
                                      </span>
                                    </button>
                                    {showLaterArchive ? (
                                      rowsLaterArchivedVisible.length === 0 ? (
                                        <p className="mt-3 text-sm text-[var(--color-text-muted)] px-1">
                                          {hideCompletedLists
                                            ? 'No lists here — completed lists stay hidden while “Hide completed lists” is checked.'
                                            : 'Nothing in Archive.'}
                                        </p>
                                      ) : (
                                      <div className="mt-3 flex flex-col gap-3">
                                        {rowsLaterArchivedVisible.map((row) => {
                                          const dragId =
                                            row.kind === 'task'
                                              ? `ws-drag:task:${row.task.id}`
                                              : `ws-drag:focus:${row.focus.id}`;
                                          const mixed = taskHasMixedOpenStatuses(row.task);
                                          const col = workspaceKanbanColumnForTask(row.task);
                                          const exitPhase = exitingWorkspaceCards[workspaceCardKey(row)];
                                          return (
                                            <div key={dragId} className={workspaceRowExitShellClass(exitPhase)}>
                                              <WorkspaceDraggableShell dragId={dragId}>
                                                <TaskCard
                                                  task={row.task}
                                                  onToggle={row.kind === 'task' ? toggle : toggleFocusItem}
                                                  onAddItem={row.kind === 'task' ? addItem : addFocusItem}
                                                  onDeleteItem={
                                                    row.kind === 'task' ? deleteItem : deleteFocusItem
                                                  }
                                                  onReorder={
                                                    row.kind === 'task' ? reorder : reorderFocusItems
                                                  }
                                                  variant={row.kind === 'day_focus' ? 'dayFocus' : 'task'}
                                                  dateBadge={dateBadgeFromCreated(row.task)}
                                                  badgeLabel={
                                                    mixed
                                                      ? `Mixed · ${WS_COLUMNS.find((c) => c.id === col)?.label ?? col}`
                                                      : undefined
                                                  }
                                                  externalDndContext
                                                />
                                              </WorkspaceDraggableShell>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      )
                                    ) : null}
                                  </div>
                                ) : null}
                              </>
                            )}
                          </>
                        ) : rowsByLaneActive[laneMeta.id].length === 0 ? (
                          <p className="text-sm text-[var(--color-text-muted)] py-2">
                            Nothing here — drag cards between lanes below or add tasks from Home or sessions.
                          </p>
                        ) : (
                          rowsByLaneActive[laneMeta.id].map((row) => {
                            const dragId =
                              row.kind === 'task'
                                ? `ws-drag:task:${row.task.id}`
                                : `ws-drag:focus:${row.focus.id}`;
                            const mixed = taskHasMixedOpenStatuses(row.task);
                            const col = workspaceKanbanColumnForTask(row.task);
                            const exitPhase = exitingWorkspaceCards[workspaceCardKey(row)];
                            return (
                              <div key={dragId} className={workspaceRowExitShellClass(exitPhase)}>
                                <WorkspaceDraggableShell dragId={dragId}>
                                  <TaskCard
                                    task={row.task}
                                    onToggle={row.kind === 'task' ? toggle : toggleFocusItem}
                                    onAddItem={row.kind === 'task' ? addItem : addFocusItem}
                                    onDeleteItem={row.kind === 'task' ? deleteItem : deleteFocusItem}
                                    onReorder={row.kind === 'task' ? reorder : reorderFocusItems}
                                    variant={row.kind === 'day_focus' ? 'dayFocus' : 'task'}
                                    badgeLabel={
                                      mixed ? `Mixed · ${WS_COLUMNS.find((c) => c.id === col)?.label ?? col}` : undefined
                                    }
                                    externalDndContext
                                  />
                                </WorkspaceDraggableShell>
                              </div>
                            );
                          })
                        )}
                      </div>
                      ) : null}
                    </div>
                  </LaneDropSurface>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col gap-10">
                {TODO_SWIM_LANE_META.map((laneMeta) => {
                  const collapsed = laneCollapsed[laneMeta.id];
                  const activeCount = laneActiveCounts[laneMeta.id];
                  return (
                  <div
                    key={laneMeta.id}
                    id={`ws-lane-${laneMeta.id}`}
                    className="flex flex-col gap-3 scroll-mt-24"
                  >
                    <button
                      type="button"
                      onClick={() => toggleLaneCollapsed(laneMeta.id)}
                      className="flex items-center gap-2 text-left rounded-lg p-1 -ml-1 hover:bg-[var(--color-surface)] transition-colors"
                      aria-expanded={!collapsed}
                      aria-controls={`ws-kan-lane-body-${laneMeta.id}`}
                    >
                      {collapsed ? (
                        <ChevronRight size={16} className="text-[var(--color-text-muted)]" />
                      ) : (
                        <ChevronDown size={16} className="text-[var(--color-text-muted)]" />
                      )}
                      <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                        {laneMeta.label}
                      </h2>
                      <span className="text-[11px] text-[var(--color-text-muted)]">
                        {activeCount === 0 ? 'nothing planned' : `${activeCount} list${activeCount === 1 ? '' : 's'}`}
                      </span>
                    </button>
                    {!collapsed ? (
                      <p className="text-[11px] text-[var(--color-text-muted)] pl-7">
                        {laneMeta.hint}
                        {laneMeta.id === 'later'
                          ? ` Date on each card is when the list was created. After ${LATER_ARCHIVE_AFTER_DAYS}+ days they go to Archive.`
                          : ''}
                      </p>
                    ) : null}
                    {!collapsed ? (
                    <>
                    <div
                      id={`ws-kan-lane-body-${laneMeta.id}`}
                      className={
                        kanbanOrientation === 'horizontal'
                          ? 'flex flex-row gap-3 overflow-x-auto pb-2'
                          : 'flex flex-col gap-3 md:flex-row md:overflow-x-auto md:pb-2'
                      }
                    >
                      {WS_COLUMNS.map((col) => {
                        const inLane = rowsByLaneActive[laneMeta.id];
                        // Project each card into the cells where it has
                        // matching items. A mixed-status card appears in
                        // multiple cells; each cell renders only its
                        // slice of the items via `visibleItemIds`. Empty
                        // cards fall into the column reported by
                        // `workspaceKanbanColumnForTask`.
                        const inCell: Array<{ row: BoardRow; visibleItemIds?: ReadonlySet<string> }> = [];
                        for (const row of inLane) {
                          if (row.task.actionItems.length === 0) {
                            if (workspaceKanbanColumnForTask(row.task) === col.id) {
                              inCell.push({ row });
                            }
                            continue;
                          }
                          const matching = row.task.actionItems
                            .filter((it) => actionItemStatus(it) === col.id)
                            .map((it) => it.id);
                          if (matching.length > 0) {
                            inCell.push({ row, visibleItemIds: new Set(matching) });
                          }
                        }
                        const dropId = `ws-kan:${laneMeta.id}:${col.id}`;
                        return (
                          <LaneDropSurface
                            key={dropId}
                            dropId={dropId}
                            className={
                              kanbanOrientation === 'horizontal'
                                // Horizontal mode = explicit overflow scroll;
                                // keep cards compact so many lanes fit.
                                ? 'flex-shrink-0 w-[min(100%,360px)] flex flex-col gap-3'
                                // Vertical mode = let columns share the row
                                // evenly; cards expand so wrapped text doesn't
                                // pile into 8-line stacks.
                                : 'flex-1 min-w-0 md:min-w-[280px] md:basis-0 flex flex-col gap-3'
                            }
                          >
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                              {col.label}
                            </p>
                            <div className="flex flex-col gap-3">
                              {inCell.length === 0 ? (
                                <div
                                  className="rounded-xl border border-dashed border-[var(--color-border)] py-8 px-3 text-center text-xs text-[var(--color-text-muted)]"
                                  style={{ background: 'var(--color-surface)' }}
                                >
                                  Drop here
                                </div>
                              ) : (
                                inCell.map(({ row, visibleItemIds }) => {
                                  const dragId =
                                    row.kind === 'task'
                                      ? `ws-drag:task:${row.task.id}`
                                      : `ws-drag:focus:${row.focus.id}`;
                                  const exitPhase = exitingWorkspaceCards[workspaceCardKey(row)];
                                  return (
                                    <div key={dragId} className={workspaceRowExitShellClass(exitPhase)}>
                                      <WorkspaceDraggableShell dragId={dragId}>
                                        <TaskCard
                                          task={row.task}
                                          onToggle={row.kind === 'task' ? toggle : toggleFocusItem}
                                          onAddItem={row.kind === 'task' ? addItem : addFocusItem}
                                          onDeleteItem={
                                            row.kind === 'task' ? deleteItem : deleteFocusItem
                                          }
                                          onReorder={
                                            row.kind === 'task' ? reorder : reorderFocusItems
                                          }
                                          variant={row.kind === 'day_focus' ? 'dayFocus' : 'task'}
                                          dateBadge={
                                            laneMeta.id === 'later'
                                              ? dateBadgeFromCreated(row.task)
                                              : undefined
                                          }
                                          // No more "Mixed steps" — each cell
                                          // now renders a single-status slice
                                          // of the card, so by definition the
                                          // visible items aren't mixed.
                                          badgeLabel={undefined}
                                          forceActionListLayout
                                          suppressViewToggleButtons
                                          externalDndContext
                                          visibleItemIds={visibleItemIds}
                                        />
                                      </WorkspaceDraggableShell>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </LaneDropSurface>
                        );
                      })}
                    </div>
                    {laneMeta.id === 'later' && rowsLaterArchived.length > 0 ? (
                      <div className="mt-1 pt-4 border-t border-dashed border-[var(--color-border)]">
                        <button
                          type="button"
                          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-left text-sm font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
                          onClick={() => setShowLaterArchive((open) => !open)}
                        >
                          {showLaterArchive ? (
                            <ChevronDown size={18} className="shrink-0" />
                          ) : (
                            <ChevronRight size={18} className="shrink-0" />
                          )}
                          <span>Archive ({rowsLaterArchivedVisible.length})</span>
                          <span className="text-xs font-normal text-[var(--color-text-muted)]">
                            Created over {LATER_ARCHIVE_AFTER_DAYS} days ago
                          </span>
                        </button>
                        {showLaterArchive ? (
                          rowsLaterArchivedVisible.length === 0 ? (
                            <p className="mt-3 text-sm text-[var(--color-text-muted)] px-1">
                              {hideCompletedLists
                                ? 'No lists here — completed lists stay hidden while “Hide completed lists” is checked.'
                                : 'Nothing in Archive.'}
                            </p>
                          ) : (
                          <div className="mt-3 flex flex-col gap-3">
                            {rowsLaterArchivedVisible.map((row) => {
                              const dragId =
                                row.kind === 'task'
                                  ? `ws-drag:task:${row.task.id}`
                                  : `ws-drag:focus:${row.focus.id}`;
                              const mixed = taskHasMixedOpenStatuses(row.task);
                              const exitPhase = exitingWorkspaceCards[workspaceCardKey(row)];
                              return (
                                <div key={dragId} className={workspaceRowExitShellClass(exitPhase)}>
                                  <WorkspaceDraggableShell dragId={dragId}>
                                    <TaskCard
                                      task={row.task}
                                      onToggle={row.kind === 'task' ? toggle : toggleFocusItem}
                                      onAddItem={row.kind === 'task' ? addItem : addFocusItem}
                                      onDeleteItem={
                                        row.kind === 'task' ? deleteItem : deleteFocusItem
                                      }
                                      onReorder={
                                        row.kind === 'task' ? reorder : reorderFocusItems
                                      }
                                      variant={row.kind === 'day_focus' ? 'dayFocus' : 'task'}
                                      dateBadge={dateBadgeFromCreated(row.task)}
                                      badgeLabel={mixed ? 'Mixed steps' : undefined}
                                      forceActionListLayout
                                      suppressViewToggleButtons
                                      externalDndContext
                                    />
                                  </WorkspaceDraggableShell>
                                </div>
                              );
                            })}
                          </div>
                          )
                        ) : null}
                      </div>
                    ) : null}
                    </>
                    ) : null}
                  </div>
                  );
                })}
              </div>
            )}

            {/* Page-level visual ghost while a drag is in flight. We render
                a lightweight summary card (title + open count) instead of
                cloning the whole TaskCard so the overlay stays cheap and
                doesn't fight the layout while it tracks the cursor. */}
            {typeof document !== 'undefined'
              ? createPortal(
                <DragOverlay zIndex={9999}>
                  {activeDrag ? (
                    <div
                      className="px-3 py-2 rounded-lg shadow-lg max-w-[320px] pointer-events-none"
                      style={{
                        background: 'var(--color-surface-elevated)',
                        border: `1px solid ${brandColors.lightBlue}`,
                        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.22)',
                      }}
                    >
                      <p className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
                        {activeDrag.kind === 'action_item' || activeDrag.kind === 'bullet_child'
                          ? 'Sub-task'
                          : 'List'}
                      </p>
                      <p
                        className="text-sm font-semibold text-[var(--color-text-primary)] line-clamp-2"
                        title={
                          activeDrag.kind === 'action_item' || activeDrag.kind === 'bullet_child'
                            ? activeDrag.row.task.actionItems.find((i) => i.id === activeDrag.itemId)?.text
                              ?? activeDrag.row.task.title
                            : activeDrag.row.task.title
                        }
                      >
                        {activeDrag.kind === 'action_item' || activeDrag.kind === 'bullet_child'
                          ? activeDrag.row.task.actionItems.find((i) => i.id === activeDrag.itemId)?.text
                            ?? activeDrag.row.task.title
                          : activeDrag.row.task.title}
                      </p>
                    </div>
                  ) : null}
                </DragOverlay>,
                document.body,
              )
              : null}
          </DndContext>
        )}
      </div>

      <CascadeConfirmSheet
        open={cascadeProjectId !== null}
        projectName={cascadeProject?.name ?? 'project'}
        targetLane={cascadeTargetLane}
        affectedActiveCount={cascadeAffectedSummary.active}
        skippedCompletedCount={cascadeAffectedSummary.completed}
        busy={cascadeBusy}
        onConfirm={runCascade}
        onCancel={() => setCascadeProjectId(null)}
      />

      <DeleteProjectSheet
        open={pendingDeleteProjectId !== null}
        projectName={
          pendingDeleteProjectId
            ? projectsById.get(pendingDeleteProjectId)?.name ?? 'project'
            : ''
        }
        counts={pendingDeleteCounts}
        busy={deleteBusy}
        onConfirm={confirmDeleteProject}
        onCancel={() => {
          setPendingDeleteProjectId(null);
          setPendingDeleteCounts(null);
        }}
      />

      <BulkArchiveSheet
        open={bulkArchiveOpen}
        counts={bulkArchiveCounts}
        busy={bulkArchiveBusy}
        onConfirm={runBulkArchive}
        onCancel={() => setBulkArchiveOpen(false)}
      />

      <CreateProjectSheet
        open={createSheetOpen}
        busy={createSheetBusy}
        onConfirm={confirmCreateProject}
        onCancel={() => {
          if (!createSheetBusy) setCreateSheetOpen(false);
        }}
      />

      <CascadeUndoToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

function DeleteProjectSheet({
  open,
  projectName,
  counts,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  projectName: string;
  counts: { tasks: number; focuses: number } | null;
  busy: boolean;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}) {
  if (!open) return null;
  const total = counts ? counts.tasks + counts.focuses : null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Delete project ${projectName}`}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.45)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className="flank-card-elevated"
        style={{
          background: 'var(--color-surface-elevated)',
          borderRadius: 18,
          padding: 20,
          width: 'min(440px, calc(100vw - 32px))',
          boxShadow: '0 16px 40px rgba(0, 0, 0, 0.24)',
          border: '1px solid var(--color-border)',
        }}
      >
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
          Delete <span className="font-bold">{projectName}</span>?
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)] mt-2">
          {total === null ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 size={14} className="animate-spin" /> Checking referenced lists…
            </span>
          ) : total === 0 ? (
            'This project is not used by any list. It will be removed permanently.'
          ) : (
            <>
              {total} list{total === 1 ? '' : 's'} reference this project. They will be moved to the Inbox before the project is deleted. Cards themselves stay intact.
            </>
          )}
        </p>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flank-btn flank-btn-secondary text-sm px-4 py-2 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || total === null}
            className="flank-btn flank-btn-primary text-sm px-4 py-2 disabled:opacity-60"
            style={{ background: 'var(--color-error)', color: 'white' }}
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" />
                Deleting…
              </span>
            ) : total === 0 ? (
              'Delete project'
            ) : (
              'Move lists to Inbox & delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Horizontal pill switcher for the Projects pivot. Lets the user "flick" between
 * one project (or Inbox) at a time instead of scrolling through every project
 * stacked vertically. Selection is persisted by the parent.
 */
/** Drop ID prefix used by the project-switcher pills. The handler in
 *  `handleWorkspaceDragEnd` parses these to flip `projectId` on the dragged
 *  card. The literal `__inbox__` suffix means "clear the project". */
const PROJECT_PILL_DROP_PREFIX = 'ws-project-pill:';
const PROJECT_PILL_INBOX_SUFFIX = '__inbox__';

function ProjectSwitcher({
  projects,
  selectedKey,
  onSelect,
  inboxCount,
}: {
  projects: TodoProject[];
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  inboxCount: number;
}) {
  // Detect drag state once for the whole pill row so every pill can light up
  // together when something is being dragged. `useDroppable.active` only
  // flips on hover, which is too late for "where can I drop this?" UX.
  const [dragging, setDragging] = useState(false);
  useDndMonitor({
    onDragStart: () => setDragging(true),
    onDragCancel: () => setDragging(false),
    onDragEnd: () => setDragging(false),
  });

  return (
    <div
      role="tablist"
      aria-label="Project switcher"
      className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1"
      style={{ scrollbarWidth: 'thin' }}
    >
      <SwitcherPill
        active={selectedKey === null}
        onClick={() => onSelect(null)}
        // "All" isn't a project assignment target — drag-to-assign is meaningless.
        droppableId={null}
        dragging={dragging}
      >
        All
      </SwitcherPill>

      <SwitcherPill
        active={selectedKey === PROJECT_SWITCHER_INBOX_KEY}
        onClick={() => onSelect(PROJECT_SWITCHER_INBOX_KEY)}
        droppableId={`${PROJECT_PILL_DROP_PREFIX}${PROJECT_PILL_INBOX_SUFFIX}`}
        dragging={dragging}
        icon={<Inbox size={13} />}
        badge={inboxCount > 0 ? String(inboxCount) : undefined}
        badgeTone={inboxCount > 0 ? 'attention' : 'idle'}
        title={inboxCount > 0 ? `${inboxCount} unassigned list${inboxCount === 1 ? '' : 's'} — drop here to clear a project` : 'Inbox'}
      >
        Inbox
      </SwitcherPill>

      {projects.map((project) => {
        const tokens = project.colorKey ? TODO_PROJECT_COLOR_TOKENS[project.colorKey] : null;
        const active = selectedKey === project.id;
        return (
          <SwitcherPill
            key={project.id}
            active={active}
            onClick={() => onSelect(project.id)}
            droppableId={`${PROJECT_PILL_DROP_PREFIX}${project.id}`}
            dragging={dragging}
            tokens={tokens ?? undefined}
            title={`${project.name} — drop a card here to add it to this project`}
            dot
          >
            {project.name}
          </SwitcherPill>
        );
      })}
    </div>
  );
}

/**
 * Single pill in the project switcher. Encapsulates the active/idle/drag-over
 * visual stack and the optional `useDroppable` wiring so the parent can stay
 * declarative.
 */
function SwitcherPill({
  active,
  onClick,
  droppableId,
  dragging,
  tokens,
  icon,
  badge,
  badgeTone,
  dot,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  /** When set, the pill becomes a drop target. */
  droppableId: string | null;
  dragging: boolean;
  tokens?: { fg: string; tint: string };
  icon?: ReactNode;
  badge?: string;
  badgeTone?: 'idle' | 'attention';
  dot?: boolean;
  title?: string;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId ?? '__noop_pill__',
    disabled: droppableId === null,
  });

  // Visual stack:
  //   - active: filled tint + colored text + colored border (project color
  //     when provided, otherwise Flank teal-blue)
  //   - drag-over: project color or teal accent + dashed border + lift
  //   - idle, dragging globally: dashed teal-blue border hint so the user
  //     can see which pills accept the drop
  //   - idle: surface chip with neutral text
  const fg = tokens?.fg ?? brandColors.lightBlue;
  const tint = tokens?.tint ?? `${brandColors.lightBlue}22`;

  let background = 'var(--color-surface)';
  let color = 'var(--color-text-secondary)';
  let border = '1px solid var(--color-border)';
  let borderStyle = 'solid';
  let transform = 'translateY(0)';

  if (active) {
    background = tint;
    color = fg;
    border = `1px solid ${fg}`;
  }
  if (dragging && droppableId) {
    if (!active) {
      border = `1.5px dashed ${fg}`;
      color = fg;
    }
  }
  if (isOver) {
    background = tint;
    color = fg;
    border = `1.5px dashed ${fg}`;
    borderStyle = 'dashed';
    transform = 'translateY(-1px) scale(1.04)';
  }

  // CSS shorthand `border` replaces the style prop, but `borderStyle` only
  // takes effect when `border` doesn't already contain a style. Easiest fix:
  // keep the merged border above and reuse `borderStyle` only as a fallback
  // signal (no-op write).
  void borderStyle;

  return (
    <button
      ref={setNodeRef}
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      title={title}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all"
      style={{ background, color, border, transform }}
    >
      {icon ? <span className="shrink-0">{icon}</span> : null}
      {dot ? (
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: fg,
            flexShrink: 0,
          }}
        />
      ) : null}
      <span>{children}</span>
      {badge ? (
        <span
          aria-label={badgeTone === 'attention' ? `${badge} new` : badge}
          className="shrink-0 inline-flex items-center justify-center text-[10px] font-semibold tabular-nums"
          style={{
            minWidth: 18,
            padding: '0 5px',
            height: 16,
            borderRadius: 999,
            background:
              badgeTone === 'attention'
                ? brandColors.lightBlue
                : 'var(--color-surface-elevated)',
            color: badgeTone === 'attention' ? 'white' : 'var(--color-text-secondary)',
            border:
              badgeTone === 'attention'
                ? `1px solid ${brandColors.lightBlue}`
                : '1px solid var(--color-border)',
          }}
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Confirmation dialog for "Archive all current lists" — sets every active +
 * completed `TaskPlan` and `DayFocus` to `abandoned` so Projects starts clean.
 * Mirrors the visual language of `DeleteProjectSheet`.
 */
function BulkArchiveSheet({
  open,
  counts,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  counts: { tasks: number; focuses: number };
  busy: boolean;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}) {
  if (!open) return null;
  const total = counts.tasks + counts.focuses;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Archive all current lists"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.45)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className="flank-card-elevated"
        style={{
          background: 'var(--color-surface-elevated)',
          borderRadius: 18,
          padding: 20,
          width: 'min(460px, calc(100vw - 32px))',
          boxShadow: '0 16px 40px rgba(0, 0, 0, 0.24)',
          border: '1px solid var(--color-border)',
        }}
      >
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
          Archive every current list?
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)] mt-2">
          {total === 0 ? (
            'There are no active lists to archive.'
          ) : (
            <>
              This retires <span className="font-semibold text-[var(--color-text-primary)]">
                {total} list{total === 1 ? '' : 's'}
              </span>{' '}
              ({counts.tasks} task plan{counts.tasks === 1 ? '' : 's'}
              {counts.focuses > 0
                ? ` + ${counts.focuses} day focus${counts.focuses === 1 ? '' : 'es'}`
                : ''}
              ) so the Projects pivot starts clean. New lists from sessions land in the Inbox so you can assign them. You can undo from the toast.
            </>
          )}
        </p>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flank-btn flank-btn-secondary text-sm px-4 py-2 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || total === 0}
            className="flank-btn flank-btn-primary text-sm px-4 py-2 disabled:opacity-60"
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" />
                Archiving…
              </span>
            ) : (
              'Archive all lists'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Flank-styled "Create project" dialog. Asks for a name (required) and an
 * optional accent color. Centered modal that mirrors `BulkArchiveSheet` /
 * `DeleteProjectSheet` chrome and respects the workspace `no-browser-dialogs`
 * rule.
 */
function CreateProjectSheet({
  open,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  busy: boolean;
  onConfirm: (name: string, colorKey?: TodoProjectColorKey) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [colorKey, setColorKey] = useState<TodoProjectColorKey>('teal');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset on each open + autofocus the input.
  useEffect(() => {
    if (!open) return;
    setName('');
    setColorKey('teal');
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  // Esc to dismiss when not busy.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;
  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !busy;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create project"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.45)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <form
        className="flank-card-elevated"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          void onConfirm(trimmed, colorKey);
        }}
        style={{
          background: 'var(--color-surface-elevated)',
          borderRadius: 18,
          padding: 20,
          width: 'min(440px, calc(100vw - 32px))',
          boxShadow: '0 16px 40px rgba(0, 0, 0, 0.24)',
          border: '1px solid var(--color-border)',
        }}
      >
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
          Create a project
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1.5">
          Group related task lists. You can rename or recolor it any time from the project menu.
        </p>

        <label className="block mt-4">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">
            Project name
          </span>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Q3 product launch"
            maxLength={120}
            disabled={busy}
            className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none focus:border-[var(--color-border-focus)]"
          />
        </label>

        <div className="mt-4">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">
            Accent color
          </span>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {TODO_PROJECT_COLOR_KEYS.map((key) => {
              const tokens = TODO_PROJECT_COLOR_TOKENS[key];
              const selected = colorKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setColorKey(key)}
                  aria-label={`Use ${key} accent`}
                  aria-pressed={selected}
                  disabled={busy}
                  className="w-7 h-7 rounded-full transition-transform"
                  style={{
                    background: tokens.fg,
                    transform: selected ? 'scale(1.12)' : 'scale(1)',
                    outline: selected
                      ? '2px solid var(--color-border-focus)'
                      : '2px solid transparent',
                    outlineOffset: 2,
                  }}
                />
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flank-btn flank-btn-secondary text-sm px-4 py-2 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="flank-btn flank-btn-primary text-sm px-4 py-2 disabled:opacity-60"
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" />
                Creating…
              </span>
            ) : (
              'Create project'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function TodosPage() {
  return (
    <TaskViewProvider
      viewModeStorageKey="flank-todos-view-mode"
      orientationStorageKey="flank-todos-kanban-orientation"
    >
      <TodosPageInner />
    </TaskViewProvider>
  );
}
